import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Box, Button, CircularProgress, FormControlLabel, FormLabel, Grid, IconButton, Modal, Radio, RadioGroup, Tab, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { API_ENDPOINT } from "../../../services/api/resources";
import SuccessPopupDashboard from "../../../components/Popups/PopupFrame/SuccessPopupDashboard";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import PaginationComponent from "../../../components/common/Pagination/PaginationComponent";
import DetailsTab from "../../../components/Dashboard/DetailsTab/DetailsTab";
import successPopupImg from "../../../assets/images/png/Group 2 2@2x.png";
import failurePopupImg from "../../../assets/images/png/Group 2@2x.png";
import bidzLogo from "../../../assets/images/png/Logo@2x.png";
import styles from "./UnifiedOnboarding.module.css";

const ONBOARDING_TYPES = [
  ["Supplier", "Company or person who supplies products or services."],
  ["Customer", "Company or person who purchases from your platform."],
  ["Dealer", "Person or company who sells your products to end customers."],
  ["Distributor", "Larger partner who distributes products to dealers or customers."],
  ["Contractor", "Works on a contract basis for projects or services."]
];

const normalizeOnboardingType = (value = "") => String(value).trim().toLowerCase();

const MASTER_ROWS_PER_PAGE = 10;
const MASTER_ONBOARDING_TYPES = ["All","supplier", "customer", "dealer", "distributor", "contractor"];
const TOOLTIP_PROPS = { tooltip: { sx: { fontSize: "13px" } } };

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return displayValue(value);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const normalizeRecordPayload = (record = {}) =>
  record?.payload_json || record?.payload || {};

const getCompanyDetails = (record = {}) => {
  const payload = normalizeRecordPayload(record);
  const company = payload?.company_details || record?.company_details || {};

  return {
    ...company,
    company_name: firstValue(company?.company_name, payload?.company_name, record?.company_name),
    company_type: firstValue(company?.company_type, payload?.company_type, record?.company_type),
    company_official_email: firstValue(company?.company_official_email, payload?.company_official_email, record?.company_official_email, record?.email),
    company_phone_number: firstValue(company?.company_phone_number, payload?.company_phone_number, record?.company_phone_number, record?.mobile),
    company_website: firstValue(company?.company_website, payload?.company_website, record?.company_website),
    company_address: firstValue(company?.company_address, payload?.company_address, record?.company_address),
    city: firstValue(company?.city, payload?.city, record?.city),
    state: firstValue(company?.state, payload?.state, record?.state),
    country: firstValue(company?.country, payload?.country, record?.country, payload?.selected_country),
    postal_code: firstValue(company?.postal_code, payload?.postal_code, record?.postal_code),
  };
};

const getContactDetails = (record = {}) => {
  const payload = normalizeRecordPayload(record);
  const contacts = payload?.contact_details || record?.contact_details || {};
  const proprietor = contacts?.proprietor || {};
  const representative = contacts?.representative || {};
  const financialUser = contacts?.financial_user || contacts?.finance_representative || {};

  return {
    ...contacts,
    proprietor: {
      first_name: firstValue(proprietor?.first_name, payload?.first_name, record?.first_name),
      last_name: firstValue(proprietor?.last_name, payload?.last_name, record?.last_name),
      designation: firstValue(proprietor?.designation, payload?.designation, record?.designation),
      official_mobile_number: firstValue(
        proprietor?.official_mobile_number,
        payload?.official_mobile_number,
        record?.official_mobile_number,
        record?.mobile,
      ),
      official_email_address: firstValue(
        proprietor?.official_email_address,
        payload?.official_email_address,
        record?.official_email_address,
        record?.email,
      ),
      phone_country_code: firstValue(proprietor?.phone_country_code, payload?.off_phn_countrycode, record?.off_phn_countrycode),
    },
    representative: {
      first_name: firstValue(representative?.first_name, payload?.rep_first_name, record?.rep_first_name),
      last_name: firstValue(representative?.last_name, payload?.rep_last_name, record?.rep_last_name),
      designation: firstValue(representative?.designation, payload?.rep_designation, record?.rep_designation),
      official_mobile_number: firstValue(
        representative?.official_mobile_number,
        payload?.rep_official_mobile_number,
        record?.rep_official_mobile_number,
      ),
      official_email_address: firstValue(
        representative?.official_email_address,
        payload?.rep_official_email_address,
        record?.rep_official_email_address,
      ),
      phone_country_code: firstValue(representative?.phone_country_code, payload?.rep_phn_countrycode, record?.rep_phn_countrycode),
    },
    financial_user: {
      first_name: firstValue(financialUser?.first_name, payload?.financerep_first_name, record?.financerep_first_name),
      last_name: firstValue(financialUser?.last_name, payload?.financerep_last_name, record?.financerep_last_name),
      designation: firstValue(financialUser?.designation, payload?.financerep_designation, record?.financerep_designation),
      official_mobile_number: firstValue(
        financialUser?.official_mobile_number,
        payload?.financerep_mobile_num,
        record?.financerep_mobile_num,
      ),
      official_email_address: firstValue(
        financialUser?.official_email_address,
        payload?.financerep_email,
        record?.financerep_email,
      ),
      phone_country_code: firstValue(financialUser?.phone_country_code, payload?.finrep_phn_countrycode, record?.finrep_phn_countrycode),
    },
  };
};

const getNestedValue = (source = {}, path = "") =>
  path.split(".").reduce((value, key) => value?.[key], source);

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") || "";

const getRecordSummary = (record = {}) => {
  const payload = normalizeRecordPayload(record);
  const company = getCompanyDetails(record);
  const contacts = getContactDetails(record);
  const proprietor = contacts?.proprietor || {};
  const representative = contacts?.representative || {};
  const gst = payload?.gst_details || record?.gst_details || {};
  const pan = payload?.pan_details || record?.pan_details || {};
  const cin = payload?.cin_details || record?.cin_details || {};
  const msme = payload?.msme_details || record?.msme_details || {};
  const bank = payload?.bank_details || record?.bank_details || {};

  return {
    supplierName: firstValue(
      record?.vendor_name,
      record?.supplier_name,
      record?.name,
      [proprietor?.first_name, proprietor?.last_name].filter(Boolean).join(" "),
      [representative?.first_name, representative?.last_name].filter(Boolean).join(" "),
      company?.company_name,
      record?.company_name,
    ),
    email: firstValue(record?.email, record?.invite_email_address, payload?.invite_email_address, company?.company_official_email, proprietor?.official_email_address),
    phone: firstValue(record?.mobile, record?.phone_number, company?.company_phone_number, proprietor?.official_mobile_number),
    companyName: firstValue(record?.company_name, company?.company_name),
    pan: firstValue(record?.pan, payload?.pan, pan?.pan_number, payload?.pan_number, record?.pan_number),
    gstin: firstValue(record?.gstin, payload?.gstin, gst?.tax_identification_number, payload?.tax_identification_number, record?.tax_identification_number),
    cin: firstValue(record?.cin, payload?.cin, cin?.company_registration_number, cin?.cin_number, payload?.company_registration_number, record?.company_registration_number),
    bankName: firstValue(bank?.bank_name, payload?.bank_name, record?.bank_name),
    bankStatus: firstValue(bank?.bank_status, payload?.bank_status, record?.bank_status),
    msmeNo: firstValue(msme?.msme_registration_number, payload?.msme_registration_number, record?.msme_registration_number),
    msmeStatus: firstValue(msme?.msme_status, payload?.msme_status, record?.msme_status),
    status: firstValue(record?.status, payload?.status, record?.onboarding_status),
    createdDate: firstValue(record?.created_on, record?.created_at, payload?.created_on),
    updatedDate: firstValue(record?.updated_on, record?.updated_at, payload?.updated_on),
  };
};

const NORMALIZED_LABELS = {
  gstin: "GST Number",
  gstnumber: "GST Number",
  taxidentificationnumber: "GST Number",
  gsttradename: "Trade Name",
  tradename: "Trade Name",
  tradenam: "Trade Name",
  gstlegalname: "Legal Name",
  legalname: "Legal Name",
  lgnm: "Legal Name",
  gstplaceofbusiness: "Place of Business",
  placeofbusiness: "Place of Business",
  constitutionofbusiness: "Constitution",
  gstnatureofbusiness: "Nature of Business",
  natureofbusiness: "Nature of Business",
  natureoftrade: "Nature of Trade",
  gsttype_of_registration: "Registration Type",
  gsttypeofregistration: "Registration Type",
  typeofregistration: "Registration Type",
  dealertype: "Registration Type",
  gstdateofvalidity: "Validity Date",
  gstdateofliability: "Liability Date",
  gstcancellationflag: "Cancellation Flag",
  cancellationflag: "Cancellation Flag",
  canflag: "Cancellation Flag",
  accountnumber: "Account No",
  accountholdername: "Account Holder",
  bankidentificationcode: "IFSC",
  branchcode: "IFSC Prefix",
  branchaddress: "Branch Address",
  accounttype: "Account Type",
  bankname: "Bank Name",
  bankstatus: "Bank Status",
  companregistrationnumber: "CIN",
  companyregistrationnumber: "CIN",
  registrationnumber: "Registration No",
  msmenameofenterprise: "Enterprise Name",
  msmeregistrationnumber: "MSME No",
  msmemajoractivity: "Major Activity",
  msmedateoffiling: "Date of Filing",
  msmetypeofenterprise: "Enterprise Type",
  panname: "PAN Name",
  pannumber: "PAN Number",
  panstatus: "PAN Status",
  pannumber: "PAN Number",
  panno: "PAN Number",
  mobile: "Mobile",
  email: "Email",
  companyname: "Company Name",
  companyofficialemail: "Official Email",
  companyphonenumber: "Phone Number",
  companyphonecountrycode: "Country Code",
  companyaddress: "Address",
  postalcode: "Postal Code",
  createdon: "Created Date",
  updatedon: "Updated Date",
  dateofincorporation: "Incorporated On",
  dateoflastagm: "Last AGM",
  dateofbalancesheet: "Balance Sheet Date",
  dateofannualreturnfiled: "Annual Return Filed",
  authorisedcapital: "Authorised Capital",
  paidupcapital: "Paid-up Capital",
  registeredaddress: "Registered Address",
  activecompliance: "Compliance",
  isemailinvalid: "Email Invalid",
  isfieldvisitconducted: "Field Visit Conducted",
  rocCode: "ROC",
  roccode: "ROC",
  entityid: "CIN",
  dty: "Registration Type",
  ctb: "Constitution",
  rgdt: "Registration Date",
  sts: "Status",
  pradr: "Principal Address",
  adr: "Address",
  ntr: "Nature of Business",
  einvoicestatus: "E-Invoice",
  aadhaarauthenticated: "Aadhaar Auth",
};

const HIDDEN_FIELD_KEYS = new Set([
  "id",
  "kid",
  "token",
  "vendortoken",
  "invite_token",
  "invitetoken",
  "requesttoken",
  "requestid",
  "request_id",
  "statuscode",
  "status-code",
  "clientdata",
  "caseid",
  "sourceuri",
  "source_uri",
  "source",
  "rawresponse",
  "apiresponse",
  "payloadjson",
  "pdfdata",
  "base64",
]);

const normalizeKey = (value = "") =>
  String(value).replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

const looksLikeUrl = (value = "") => /^(https?:|blob:|data:)/i.test(String(value));

const isFilePathLike = (value = "") => {
  const text = String(value || "");
  return /\.(pdf|png|jpg|jpeg|webp|gif|doc|docx|xls|xlsx)(\?|$)/i.test(text) && /[\\/]/.test(text);
};

const shouldHideField = (key = "", value = "") => {
  const normalized = normalizeKey(key);
  const rawKey = String(key).toLowerCase();

  if (!normalized) return false;
  if (HIDDEN_FIELD_KEYS.has(normalized)) return true;
  if (rawKey.includes("token")) return true;
  if (rawKey.includes("requestid") || rawKey.includes("request_id")) return true;
  if (rawKey === "statuscode" || rawKey === "status-code") return true;
  if (["url", "documenturl", "fileurl", "filepath", "path", "source"].includes(normalized)) return true;
  if (typeof value === "string" && (looksLikeUrl(value) || isFilePathLike(value))) return true;

  return false;
};

const getCleanLabel = (value = "") => {
  const normalized = normalizeKey(value);
  if (NORMALIZED_LABELS[normalized]) return NORMALIZED_LABELS[normalized];

  return String(value)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const buildDetailRows = (source = {}, fields = []) =>
  fields
    .map(({ label, path, value }) => [getCleanLabel(label), displayValue(value !== undefined ? value : getNestedValue(source, path))])
    .filter(([label, value]) => !shouldHideField(label, value));

const formatFieldLabel = getCleanLabel;

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const flattenFields = (source, prefix = "", depth = 0) => {
  if (!source || depth > 2) return [];

  if (Array.isArray(source)) {
    return source.flatMap((item, index) =>
      isPlainObject(item) || Array.isArray(item)
        ? flattenFields(item, `${prefix}${prefix ? " " : ""}${index + 1}`, depth + 1)
        : [[`${prefix} ${index + 1}`.trim(), displayValue(item)]],
    );
  }

  if (!isPlainObject(source)) {
    return [[prefix || "Value", displayValue(source)]];
  }

  return Object.entries(source).flatMap(([key, value]) => {
    if (shouldHideField(key, value)) return [];
    const nextLabel = [prefix, getCleanLabel(key)].filter(Boolean).join(" - ");
    if (value === null || value === undefined || value === "") return [[nextLabel, "-"]];
    if (isPlainObject(value) || Array.isArray(value)) return flattenFields(value, nextLabel, depth + 1);
    return [[nextLabel, displayValue(value)]];
  });
};

const row = (label, value) => [getCleanLabel(label), displayValue(value)];

const cleanRows = (rows = []) =>
  rows.filter(([label, value]) => value && value !== "-" && !shouldHideField(label, value));

const toArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const compactList = (value) => {
  if (!Array.isArray(value)) return displayValue(value);
  const visible = value.filter((item) => item !== null && item !== undefined && String(item).trim() !== "");
  return visible.length ? visible.join(", ") : "-";
};

const formatAmount = (value, includeCurrency = false) => {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(numeric)) return displayValue(value);
  const formatted = numeric.toLocaleString("en-IN");
  return includeCurrency ? `₹ ${formatted}` : formatted;
};

const firstObject = (...values) =>
  values.find((value) => isPlainObject(value) && Object.keys(value).length) || {};

const firstArray = (...values) =>
  values.find((value) => Array.isArray(value) && value.length) || [];

const getVerificationResult = (...responses) => {
  for (const response of responses) {
    const result = response?.verification?.result;
    if (Array.isArray(result) && result.length) return result[0];
    if (isPlainObject(result)) return result;
  }
  return {};
};

const getDocumentSource = (...values) => {
  const source = values.find((value) => {
    if (!value) return false;
    const text = String(value);
    return text !== "file" && (looksLikeUrl(text) || isFilePathLike(text) || text.startsWith("/"));
  });
  return source ? String(source) : "";
};

const getDocumentLinkFromMap = (fileLinks = {}, field = "", aliases = []) => {
  if (!isPlainObject(fileLinks)) return "";
  const keys = [field, ...aliases].filter(Boolean);
  const matchKey = keys.find((key) => fileLinks?.[key]);
  return matchKey ? fileLinks[matchKey] : "";
};

const getFileNameFromSource = (source = "") => {
  if (!source) return "";
  try {
    const clean = String(source).split("?")[0];
    return decodeURIComponent(clean.substring(clean.lastIndexOf("/") + 1));
  } catch (error) {
    return String(source).split("?")[0].split("/").pop();
  }
};

const getDocumentType = (document = {}) => {
  const text = `${document.fileType || ""} ${document.fileName || ""} ${document.source || ""}`.toLowerCase();
  if (text.includes("pdf") || text.endsWith(".pdf")) return "pdf";
  if (/image|\.png|\.jpg|\.jpeg|\.webp|\.gif/.test(text)) return "image";
  if (/doc|docx/.test(text)) return "doc";
  if (/xls|xlsx|csv/.test(text)) return "sheet";
  return "file";
};

const dedupeDocumentCards = (cards = []) => {
  const seen = new Set();

  return cards.filter((card) => {
    const labelKey = String(card?.label || "").trim().toLowerCase();
    const sourceKey = String(card?.source || "").trim().toLowerCase();
    const fileKey = String(card?.fileName || "").trim().toLowerCase();
    const dedupeKey = `${labelKey}::${sourceKey || fileKey}`;
    const isGenericDocumentLabel = ["document", "documents", "file"].includes(labelKey);

    if (!sourceKey) return false;
    if (isGenericDocumentLabel) return false;
    if (seen.has(dedupeKey)) return false;

    seen.add(dedupeKey);
    return true;
  });
};

const buildDocumentCards = (record = {}) => {
  const payload = normalizeRecordPayload(record);
  const uploadedDocuments = payload?.uploaded_documents || record?.uploaded_documents || [];
  const documentDetails = payload?.document_details || record?.document_details || {};
  const fileLinks = payload?.file_links || payload?.file_link || record?.file_links || record?.file_link || {};
  const documentConfig = [
    { label: "Business Registration", field: "business_reg_certificate", aliases: ["business_reg_certificate", "business"] },
    { label: "PAN Card", field: "pan_card", aliases: ["pan_card", "pan"] },
    { label: "Cancelled Cheque", field: "one_cancelled_cheque_leaf", aliases: ["one_cancelled_cheque_leaf", "bank", "cancelled_cheque"] },
    { label: "GST Certificate", field: "tax_registration_certificate", aliases: ["tax_registration_certificate", "gst"] },
    { label: "MSME Certificate", field: "msme_certificate", aliases: ["msme_certificate", "msme"] },
    { label: "TDS Certificate", field: "tds_certificate", aliases: ["tds_certificate", "tds"] },
    { label: "CIN Certificate", field: "corporate_identity_certificate", aliases: ["corporate_identity_certificate", "cin"] },
  ];

  const configuredCards = documentConfig.map(({ label, field, aliases }) => {
    const detail = documentDetails?.[field] || {};
    const uploaded = Array.isArray(uploadedDocuments)
      ? uploadedDocuments.find((document) =>
          aliases.includes(document?.field_name) || aliases.includes(document?.key),
        ) || {}
      : {};
    const file = uploaded?.file || detail?.file || {};
    const source = getDocumentSource(
      getDocumentLinkFromMap(fileLinks, field, aliases),
      file?.file_link,
      file?.url,
      file?.path,
      file?.document_url,
      file?.file_url,
      file?.preview_url,
      file?.source,
      uploaded?.file_link,
      uploaded?.url,
      uploaded?.path,
      uploaded?.document_url,
      uploaded?.file_url,
      uploaded?.preview_url,
      detail?.file_link,
      detail?.url,
      detail?.path,
      detail?.document_url,
      detail?.file_url,
      detail?.preview_url,
    );
    const fileName = firstValue(
      file?.name,
      uploaded?.name,
      detail?.name,
      payload?.[field],
      record?.[field],
      getFileNameFromSource(source),
    );

    return {
      label: firstValue(uploaded?.name, label),
      identifier: uploaded?.identifier,
      status: firstValue(uploaded?.status, detail?.status),
      required: uploaded?.required,
      fileName,
      fileType: file?.type,
      fileSize: file?.size,
      source,
    };
  });

  const extraUploadedCards = Array.isArray(uploadedDocuments)
    ? uploadedDocuments
        .filter((document) => !documentConfig.some(({ aliases }) => aliases.includes(document?.field_name) || aliases.includes(document?.key)))
        .map((document) => {
          const file = document?.file || {};
          const source = getDocumentSource(
            getDocumentLinkFromMap(fileLinks, document?.field_name || document?.key, [document?.field_name, document?.key].filter(Boolean)),
            file?.file_link,
            file?.url,
            file?.path,
            file?.document_url,
            file?.file_url,
            file?.preview_url,
            file?.source,
            document?.file_link,
            document?.url,
            document?.path,
            document?.document_url,
            document?.file_url,
            document?.preview_url,
          );
          return {
            label: firstValue(document?.name, getCleanLabel(document?.field_name || document?.key || "Document")),
            identifier: document?.identifier,
            status: document?.status,
            required: document?.required,
            fileName: firstValue(file?.name, getFileNameFromSource(source)),
            fileType: file?.type,
            fileSize: file?.size,
            source,
          };
        })
    : [];

  return dedupeDocumentCards([...configuredCards, ...extraUploadedCards]);
};

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined || value === "") return false;
  if (typeof value === "boolean") return value;
  if (Array.isArray(value)) return value.some(hasMeaningfulValue);
  if (isPlainObject(value)) return Object.values(value).some(hasMeaningfulValue);
  return String(value).trim() !== "" && String(value).trim() !== "-";
};

const isPositiveVerificationStatus = (value) =>
  ["verified", "active", "valid", "success", "successful transaction", "kc01"].includes(
    String(value || "").trim().toLowerCase(),
  );

const getOnboardingType = (record = {}) => {
  const payload = normalizeRecordPayload(record);
  return firstValue(record?.onboarding_type, payload?.onboarding_type, record?.vendor_type, payload?.vendor_type, "supplier");
};

const documentExists = (record = {}, aliases = []) => {
  const payload = normalizeRecordPayload(record);
  const uploadedDocuments = payload?.uploaded_documents || record?.uploaded_documents || [];
  const documentDetails = payload?.document_details || record?.document_details || {};
  const fileLinks = payload?.file_links || payload?.file_link || record?.file_links || record?.file_link || {};

  const uploadedMatch = Array.isArray(uploadedDocuments)
    ? uploadedDocuments.find((document) =>
        aliases.includes(document?.key) || aliases.includes(document?.field_name),
      )
    : null;
  const detailMatch = aliases.map((alias) => documentDetails?.[alias]).find(Boolean);
  const uploadedFile = uploadedMatch?.file || {};
  const detailFile = detailMatch?.file || {};
  const hasFlatDocument = aliases.some((alias) =>
    hasMeaningfulValue(payload?.[alias])
    || hasMeaningfulValue(record?.[alias])
    || hasMeaningfulValue(fileLinks?.[alias]),
  );

  return hasFlatDocument
    || hasMeaningfulValue(uploadedMatch?.identifier)
    || hasMeaningfulValue(uploadedMatch?.status)
    || hasMeaningfulValue(uploadedFile?.name)
    || hasMeaningfulValue(uploadedFile?.url)
    || hasMeaningfulValue(detailMatch?.status)
    || hasMeaningfulValue(detailFile?.name)
    || hasMeaningfulValue(detailFile?.url);
};

const getUnifiedOnboardingStatus = (record = {}) => {
  const payload = normalizeRecordPayload(record);
  const gst = payload?.gst_details || record?.gst_details || {};
  const pan = payload?.pan_details || record?.pan_details || {};
  const cin = payload?.cin_details || record?.cin_details || {};
  const msme = payload?.msme_details || record?.msme_details || {};
  const bank = payload?.bank_details || record?.bank_details || {};
  const bankSourceData =
    payload?.bank_response?.verification?.result?.data?.source?.[0]?.data
    || record?.bank_response?.verification?.result?.data?.source?.[0]?.data
    || {};

  const gstValue = firstValue(
    record?.gstin,
    payload?.gstin,
    gst?.tax_identification_number,
    payload?.tax_identification_number,
    record?.tax_identification_number,
    gst?.gst_number,
  );
  const panValue = firstValue(
    record?.pan,
    payload?.pan,
    pan?.pan_number,
    payload?.pan_number,
    record?.pan_number,
  );
  const bankValue = firstValue(
    bank?.account_number,
    bank?.bank_name,
    bank?.bank_identification_code,
    payload?.account_number,
    record?.account_number,
    payload?.bank_name,
    record?.bank_name,
    payload?.bank_identification_code,
    record?.bank_identification_code,
    bankSourceData?.accountNumber,
    bankSourceData?.accountName,
  );
  const cinValue = firstValue(
    record?.cin,
    payload?.cin,
    cin?.company_registration_number,
    cin?.cin_number,
    payload?.company_registration_number,
    record?.company_registration_number,
    payload?.cin_status,
    record?.cin_status,
  );
  const msmeValue = firstValue(
    msme?.msme_registration_number,
    msme?.msme_name_of_enterprise,
    msme?.msme_status,
    payload?.msme_registration_number,
    record?.msme_registration_number,
    payload?.msme_name_of_enterprise,
    record?.msme_name_of_enterprise,
    payload?.msme_status,
    record?.msme_status,
  );

  const hasGst = hasMeaningfulValue(gstValue)
    || isPositiveVerificationStatus(firstValue(gst?.gst_status, payload?.gst_status, record?.gst_status))
    || documentExists(record, ["gst", "tax_registration_certificate"]);
  const hasPan = hasMeaningfulValue(panValue)
    || isPositiveVerificationStatus(firstValue(pan?.pan_status, payload?.pan_status, record?.pan_status))
    || documentExists(record, ["pan", "pan_card"]);
  const hasBank = hasMeaningfulValue(bankValue)
    || isPositiveVerificationStatus(firstValue(bank?.bank_status, payload?.bank_status, record?.bank_status, bankSourceData?.statusCode, bankSourceData?.bankResponse))
    || documentExists(record, ["bank", "one_cancelled_cheque_leaf", "cancelled_cheque"]);
  const hasCin = hasMeaningfulValue(cinValue)
    || isPositiveVerificationStatus(firstValue(cin?.cin_status, payload?.cin_status, record?.cin_status))
    || documentExists(record, ["cin", "corporate_identity_certificate"]);
  const hasMsme = hasMeaningfulValue(msmeValue)
    || isPositiveVerificationStatus(firstValue(msme?.msme_status, payload?.msme_status, record?.msme_status))
    || documentExists(record, ["msme", "msme_certificate"]);

  const cinApplicable = hasMeaningfulValue(cinValue)
    || isPositiveVerificationStatus(firstValue(cin?.cin_status, payload?.cin_status, record?.cin_status))
    || documentExists(record, ["cin", "corporate_identity_certificate"]);
  const msmeApplicable = hasMeaningfulValue(msmeValue)
    || isPositiveVerificationStatus(firstValue(msme?.msme_status, payload?.msme_status, record?.msme_status))
    || documentExists(record, ["msme", "msme_certificate"]);

  if (hasGst && hasPan && hasBank && (!cinApplicable || hasCin) && (!msmeApplicable || hasMsme)) {
    return "Fully Onboarded";
  }

  return "Partially Onboarded";
};

const buildPdfTableSectionRows = (rows = [], columns = [], itemLabel = "Row") =>
  toArray(rows)
    .filter((item) => isPlainObject(item) && Object.values(item).some((value) => displayValue(value) !== "-"))
    .flatMap((item, index) =>
      columns
        .map((column) => {
          const rawValue = column.render ? column.render(item, index) : item?.[column.key];
          return row(`${itemLabel} ${index + 1} - ${column.label}`, rawValue);
        })
        .filter(([, value]) => value && value !== "-"),
    );

const buildUnifiedOnboardingPdfSections = (record = {}) => {
  const payload = normalizeRecordPayload(record);
  const summary = getRecordSummary(record);
  const company = getCompanyDetails(record);
  const contacts = getContactDetails(record);
  const proprietor = contacts?.proprietor || {};
  const representative = contacts?.representative || {};
  const financialUser = contacts?.financial_user || contacts?.finance_representative || {};
  const gst = payload?.gst_details || record?.gst_details || {};
  const pan = payload?.pan_details || record?.pan_details || {};
  const cin = payload?.cin_details || record?.cin_details || {};
  const msme = payload?.msme_details || record?.msme_details || {};
  const bank = payload?.bank_details || record?.bank_details || {};

  const gstResponse = payload?.gst_response || record?.gst_response || payload?.verification_responses?.gst || record?.verification_responses?.gst || {};
  const gstVerification = gstResponse?.verification || {};
  const gstResult = firstObject(gstVerification?.result, payload?.verification_responses?.gst?.verification?.result);
  const gstProfile = firstObject(
    gstVerification?.gstProfile?.result?.[0],
    payload?.verification_responses?.gst?.verification?.gstProfile?.result?.[0],
  );

  const panResponse = payload?.pan_response || record?.pan_response || payload?.verification_responses?.pan || record?.verification_responses?.pan || {};
  const panProfile = firstObject(
    panResponse?.verification?.panProfile?.result,
    panResponse?.panProfile?.result,
    payload?.panProfile?.result,
    record?.panProfile?.result,
    panResponse?.verification?.result,
  );

  const cinResponse = payload?.cin_response || record?.cin_response || payload?.verification_responses?.cin || record?.verification_responses?.cin || {};
  const mcaResponse = payload?.mca_response || record?.mca_response || payload?.verification_responses?.mca || record?.verification_responses?.mca || {};
  const cinCompany = getVerificationResult(cinResponse, mcaResponse);
  const combinedCinRiskFlags = {
    ...(cin?.cin_blacklist_flags || {}),
    isMlm: firstValue(cinResponse?.verification?.isMlm, cinCompany?.isMlm, cin?.cin_blacklist_flags?.isMlm),
    isPep: firstValue(cinResponse?.verification?.isPep, cinCompany?.isPep, cin?.cin_blacklist_flags?.isPep),
    isShell: firstValue(cinResponse?.verification?.isShell, cinCompany?.isShell, cin?.cin_blacklist_flags?.isShell),
    isFormerPep: firstValue(cinResponse?.verification?.isFormerPep, cinCompany?.isFormerPep, cin?.cin_blacklist_flags?.isFormerPep),
    isVanishing: firstValue(cinResponse?.verification?.isVanishing, cinCompany?.isVanishing, cin?.cin_blacklist_flags?.isVanishing),
    isNotFiled5inv: firstValue(cinResponse?.verification?.isNotFiled5inv, cinCompany?.isNotFiled5inv, cin?.cin_blacklist_flags?.isNotFiled5inv),
    isDomainInvalid_: firstValue(cinResponse?.verification?.isDomainInvalid_, cinCompany?.isDomainInvalid_, cin?.cin_blacklist_flags?.isDomainInvalid_),
    isCompanyUnderCirp: firstValue(cinResponse?.verification?.isCompanyUnderCirp, cinCompany?.isCompanyUnderCirp, cin?.cin_blacklist_flags?.isCompanyUnderCirp),
    isEmailDisposable_: firstValue(cinResponse?.verification?.isEmailDisposable_, cinCompany?.isEmailDisposable_, cin?.cin_blacklist_flags?.isEmailDisposable_),
    isCompanyStrikedOff: firstValue(cinResponse?.verification?.isCompanyStrikedOff, cinCompany?.isCompanyStrikedOff, cin?.cin_blacklist_flags?.isCompanyStrikedOff),
    isMcaCompanyDefaulter: firstValue(cinResponse?.verification?.isMcaCompanyDefaulter, cinCompany?.isMcaCompanyDefaulter, cin?.cin_blacklist_flags?.isMcaCompanyDefaulter),
    isCompanyUnderStrikeOff: firstValue(cinResponse?.verification?.isCompanyUnderStrikeOff, cinCompany?.isCompanyUnderStrikeOff, cin?.cin_blacklist_flags?.isCompanyUnderStrikeOff),
    isCompanyStrikedOffUs248: firstValue(cinResponse?.verification?.isCompanyStrikedOffUs248, cinCompany?.isCompanyStrikedOffUs248, cin?.cin_blacklist_flags?.isCompanyStrikedOffUs248),
    isCompanyUnderLiquidation: firstValue(cinResponse?.verification?.isCompanyUnderLiquidation, cinCompany?.isCompanyUnderLiquidation, cin?.cin_blacklist_flags?.isCompanyUnderLiquidation),
    isLlpStrikedOffUnderRule37: firstValue(cinResponse?.verification?.isLlpStrikedOffUnderRule37, cinCompany?.isLlpStrikedOffUnderRule37, cin?.cin_blacklist_flags?.isLlpStrikedOffUnderRule37),
    isSuspendedAtStockExchange: firstValue(cinResponse?.verification?.isSuspendedAtStockExchange, cinCompany?.isSuspendedAtStockExchange, cin?.cin_blacklist_flags?.isSuspendedAtStockExchange),
    isCompanyActiveNonCompliant: firstValue(cinResponse?.verification?.isCompanyActiveNonCompliant, cinCompany?.isCompanyActiveNonCompliant, cin?.cin_blacklist_flags?.isCompanyActiveNonCompliant),
    isAddressQualityPoor_: cinCompany?.isAddressQualityPoor_,
  };

  const msmeResponse = payload?.msme_response || record?.msme_response || payload?.verification_responses?.msme || record?.verification_responses?.msme || {};
  const msmeLookup = firstObject(msmeResponse?.panLookup?.result?.[0], msmeResponse?.udyamLookup?.result?.[0], msmeResponse?.lookup?.result?.[0]);
  const msmeVerification = msmeResponse?.verification || {};
  const msmeResult = firstObject(msmeVerification?.result, msmeResponse?.result);
  const msmeProfile = firstObject(msmeResult?.profile);
  const msmeOfficialAddress = firstObject(msmeResult?.officialAddress);
  const msmeBankDetails = firstObject(msmeResult?.bankDetails);
  const msmeEmploymentDetails = firstObject(msmeResult?.employmentDetails);
  const msmeFinancialRoot = firstObject(msmeResult?.financials);
  const msmeFinancialDetails = firstObject(msmeFinancialRoot?.details, msmeFinancialRoot);
  const msmeIndustry = firstArray(msmeResult?.industry);
  const msmeEnterpriseTypes = firstArray(msmeResult?.enterpriseType);
  const msmeBranchDetails = firstArray(msmeResult?.branchDetails);

  const bankResponse = payload?.bank_response || record?.bank_response || payload?.verification_responses?.bank || record?.verification_responses?.bank || {};
  const bankVerification = bankResponse?.verification || {};
  const bankSource = bankVerification?.result?.data?.source?.[0] || {};
  const bankSourceData = bankSource?.data || {};
  const ifscDetails = bankVerification?.ifscDetails || {};

  const documentCards = buildDocumentCards(record);
  const gstBranches = [
    ...toArray(gstResult?.adadr),
    ...toArray(gstProfile?.branches),
  ];
  const gstGoods = firstArray(gstProfile?.goodsDetails, gstResult?.goodsDetails);
  const gstServices = firstArray(gstProfile?.servicesDetails, gstResult?.servicesDetails);
  const turnoverRows = gstProfile?.aggregateTurnovers;
  const filingFrequencyRows = gstProfile?.filingFrequencies;
  const directors = cinCompany?.directors;
  const formerDirectors = cinCompany?.formerDirectors;
  const charges = cinCompany?.charges;
  const riskChecks = cleanRows(flattenFields(combinedCinRiskFlags));
  const msmeIndustryRows = msmeIndustry;
  const msmeClassificationRows = msmeEnterpriseTypes;
  const msmeBranchRows = msmeBranchDetails;

  const sections = [];

  const pushSection = (title, icon, rows) => {
    const visibleRows = cleanRows(rows);
    if (visibleRows.length) {
      sections.push({ title, icon, rows: visibleRows });
    }
  };

  pushSection("Record Summary", "R", [
    row("Company", maskEntityName(firstValue(summary.companyName, summary.supplierName))),
    row("Onboarding Type", getOnboardingType(record)),
    row("Onboarding Status", getUnifiedOnboardingStatus(record)),
    row("Email", summary.email),
    row("Mobile", summary.phone),
    row("PAN", summary.pan),
    row("GST", summary.gstin),
    row("CIN", summary.cin),
    row("MSME", summary.msmeNo),
    row("Bank", summary.bankName),
    row("Created Date", formatDate(summary.createdDate)),
    row("Updated Date", formatDate(summary.updatedDate)),
  ]);

  pushSection("GST Details", "G", [
    row("GST Number", firstValue(gst?.tax_identification_number, gstResult?.gstin, gstProfile?.gstin, record?.gstin)),
    row("Trade Name", firstValue(gst?.gst_trade_name, gstResult?.tradeNam, gstProfile?.tradeName)),
    row("Legal Name", firstValue(gst?.gst_legal_name, gstResult?.lgnm, gstProfile?.legalName)),
    row("GST Place of Business", firstValue(gst?.gst_place_of_business, gstResult?.ctb, gstProfile?.constitutionOfBusiness)),
    row("Nature of Business", firstValue(gst?.gst_nature_of_business, compactList(gstResult?.nba), compactList(gstProfile?.natureOfBusiness))),
    row("Date of Liability", firstValue(gst?.gst_date_of_liability, gstResult?.rgdt, gstProfile?.dateOfRegistration)),
    row("Date of Validity", firstValue(gst?.gst_date_of_validity, gstResult?.rgdt, gstProfile?.gstApprovalDate)),
    row("Type of Registration", firstValue(gst?.gst_type_of_registration, gstResult?.dty, gstProfile?.dealerType)),
    row("Cancellation Flag", firstValue(gst?.gst_cancellation_flag, gstResult?.canFlag, gstProfile?.cancellationFlag)),
  ]);

  pushSection("Principal Address", "G", [
    row("Address", firstValue(gstResult?.pradr?.adr, gstProfile?.address)),
    row("Nature of Business", firstValue(gstResult?.pradr?.ntr, gstProfile?.natureOfTrade)),
    row("State", gstProfile?.state_),
    row("Contact", gstProfile?.contact),
    row("Email", gstProfile?.email),
  ]);

  pushSection("Additional Places / Branches", "G", gstBranches.flatMap((item, index) => cleanRows([
    row(`Branch ${index + 1} - Address`, firstValue(item?.adr, item?.address)),
    row(`Branch ${index + 1} - Nature`, firstValue(item?.ntr, item?.natureOfTrade)),
    row(`Branch ${index + 1} - Contact`, item?.contact),
    row(`Branch ${index + 1} - Email`, item?.email),
  ])));

  pushSection("Goods / HSN Details", "G", buildPdfTableSectionRows(gstGoods, [
    { key: "hsnCode", label: "HSN Code", render: (item) => item?.hsnCode },
    { key: "chapterCode", label: "Chapter", render: (item) => item?.chapterCode },
    { key: "description", label: "Description", render: (item) => firstValue(item?.hsn1, item?.hsn2, item?.chapter) },
  ], "HSN"));

  pushSection("Services / SAC Details", "G", buildPdfTableSectionRows(gstServices, [
    { key: "sacCode", label: "SAC Code", render: (item) => item?.sacCode },
    { key: "service", label: "Service", render: (item) => firstValue(item?.sac1, item?.sac2) },
  ], "Service"));

  pushSection("GST Compliance", "G", [
    row("PAN Profile Status", panProfile?.status),
    row("Aggregate Turnover", firstValue(gstProfile?.aggregateTurnover, gstProfile?.aggregateTurnovers?.[0]?.turnover)),
    row("Aadhaar Auth", gstProfile?.aadhaarAuthenticated),
    row("Last Updated", firstValue(gstResult?.lstupdt, gstProfile?.lastUpdatedDate)),
    row("State Jurisdiction", gstProfile?.stateJurisdiction),
    row("Centre Jurisdiction", gstProfile?.centreJurisdiction),
    ...buildPdfTableSectionRows(turnoverRows, [
      { key: "financialYear", label: "Turnover FY" },
      { key: "turnover", label: "Turnover" },
    ], "Turnover"),
    ...buildPdfTableSectionRows(filingFrequencyRows, [
      { key: "financialYear", label: "Filing FY" },
      { key: "frequency", label: "Frequency", render: (item) => toArray(item?.frequencies).map((fq) => `${fq?.quarter}: ${fq?.preference}`).join(", ") },
    ], "Filing"),
  ]);

  pushSection("PAN Details", "P", [
    row("PAN Number", firstValue(pan?.pan_number, payload?.pan_number, record?.pan_number, record?.pan)),
    row("PAN Name", firstValue(pan?.pan_name, payload?.pan_name, record?.pan_name, panProfile?.name)),
    row("PAN Status", firstValue(pan?.pan_status, payload?.pan_status, record?.pan_status, panProfile?.status)),
    row("PAN Profile Status", panProfile?.status),
    row("Date of Birth", panProfile?.dob),
  ]);

  pushSection("Business / CIN Details", "B", [
    row("CIN", firstValue(cin?.company_registration_number, cin?.cin_number, payload?.company_registration_number, record?.company_registration_number, cinCompany?.entityId, record?.cin)),
    row("CIN Status", firstValue(cin?.cin_status, payload?.cin_status, record?.cin_status, cinCompany?.status)),
    row("MCA Status", cinCompany?.status),
    row("Company Name", maskEntityName(firstValue(cinCompany?.name, record?.company_name, company?.company_name))),
    row("MCA Email", cinCompany?.email),
    row("Associated PAN", maskListValues(cinCompany?.pans)),
    row("Class", cinCompany?.class),
    row("Category", cinCompany?.category),
    row("Subcategory", cinCompany?.subcategory),
    row("Company Type", cinCompany?.type_),
    row("Registration No", cinCompany?.registrationNumber),
    row("MCA Constitution Code", cinCompany?.mcaConstitutionCode_),
    row("Industry", cinCompany?.industry_),
    row("Sub Industry", cinCompany?.subIndustry_),
    row("Email Domain", cinCompany?.emailDomain),
    row("Country", cinCompany?.countryOfIncorporation),
    row("ROC", cinCompany?.rocCode),
    row("Incorporated On", cinCompany?.dateOfIncorporation),
    row("Last AGM", cinCompany?.dateOfLastAgm),
    row("Balance Sheet Date", cinCompany?.dateOfBalanceSheet),
    row("Annual Return Filed", cinCompany?.dateOfAnnualReturnFiled),
    row("Listing Status", cinCompany?.listingStatus),
    row("Compliance", cinCompany?.activeCompliance),
    row("Authorised Capital", formatAmount(cinCompany?.authorisedCapital)),
    row("Paid-up Capital", formatAmount(cinCompany?.paidUpCapital)),
    row("Members", cinCompany?.numberOfMembers),
    row("Partners", cinCompany?.numberOfPartners),
    row("Designated Partners", cinCompany?.numberOfDesignatedPartners),
    row("Registered Address", cinCompany?.registeredAddress),
    row("Activity Class", cinCompany?.activityClass_),
    row("Activity Group", cinCompany?.activityGroup_),
    row("Activity Sub Class", cinCompany?.activitySubClass_),
  ]);

  pushSection("Directors", "B", buildPdfTableSectionRows(directors, [
    { key: "name", label: "Name", render: (item) => maskEntityName(item?.name) },
    { key: "designation", label: "Designation" },
    { key: "din", label: "DIN" },
    { key: "pan", label: "PAN", render: (item) => maskPan(item?.pan) },
    { key: "dateOfAppointment", label: "Appointed On" },
    { key: "tenureBeginDate", label: "Tenure Start" },
    { key: "tenureEndDate", label: "Tenure End" },
  ], "Director"));

  pushSection("Former Directors", "B", buildPdfTableSectionRows(formerDirectors, [
    { key: "name", label: "Name", render: (item) => maskEntityName(item?.name) },
    { key: "designation", label: "Designation" },
    { key: "din", label: "DIN" },
    { key: "pan", label: "PAN", render: (item) => maskPan(item?.pan) },
    { key: "tenureBeginDate", label: "Start" },
    { key: "tenureEndDate", label: "End" },
  ], "Former Director"));

  pushSection("Charges", "B", buildPdfTableSectionRows(charges, [
    { key: "chargeHolderName", label: "Holder" },
    { key: "chargeHolderNameStandardized_", label: "Std. Holder" },
    { key: "amount", label: "Amount", render: (item) => formatAmount(item?.amount, true) },
    { key: "status", label: "Status" },
    { key: "srn", label: "SRN" },
    { key: "chargeId", label: "Charge ID" },
    { key: "chargeHolderTag_", label: "Holder Type" },
    { key: "dateOfCreation", label: "Created On" },
    { key: "dateOfModification", label: "Modified On" },
    { key: "dateOfSatisfaction", label: "Satisfied On" },
    { key: "address", label: "Address" },
  ], "Charge"));

  pushSection("Risk / Compliance Checks", "B", riskChecks);

  pushSection("MSME Details", "M", [
    row("MSME No", firstValue(msme?.msme_registration_number, payload?.msme_registration_number, record?.msme_registration_number, msmeResult?.udyamRegistrationNo, msmeLookup?.urn)),
    row("Enterprise Name", firstValue(msme?.msme_name_of_enterprise, payload?.msme_name_of_enterprise, record?.msme_name_of_enterprise, msmeProfile?.name, msmeLookup?.name)),
    row("Enterprise Type", firstValue(msme?.msme_type_of_enterprise, payload?.msme_type_of_enterprise, record?.msme_type_of_enterprise, msmeEnterpriseTypes?.[0]?.enterpriseType)),
    row("Major Activity", firstValue(msme?.msme_major_activity, payload?.msme_major_activity, record?.msme_major_activity, msmeProfile?.majorActivity)),
    row("Date of Filing", firstValue(msme?.msme_date_of_filing, payload?.msme_date_of_filing, record?.msme_date_of_filing, msmeResult?.dateOfRegistration)),
    row("Date of Registration", msmeResult?.dateOfRegistration),
    row("MSME Status", firstValue(msme?.msme_status, payload?.msme_status, record?.msme_status)),
    row("Organization Type", msmeProfile?.organizationType),
    row("Social Category", msmeProfile?.socialCategory),
    row("Specially Abled", msmeProfile?.speciallyAbled),
    row("Date of Commencement", msmeProfile?.dateOfCommencement),
    row("Date of Incorporation", msmeProfile?.dateOfIncorporation),
    row("Owner Name", msmeProfile?.ownerName),
    row("Gender", msmeProfile?.gender),
    row("GSTN Status", msmeProfile?.gstnStatus),
    row("ITR Type", msmeProfile?.itrType),
    row("Previous ITR Status", msmeProfile?.previousITRStatus),
    row("Interested In GeM", msmeResult?.interestedInGem),
    row("Interested In TReDS", msmeResult?.interestedInTreds),
  ]);

  pushSection("MSME Official Address", "M", [
    row("Flat / Door No", msmeOfficialAddress?.flat),
    row("Premises", msmeOfficialAddress?.premises),
    row("Road", msmeOfficialAddress?.road),
    row("Village", msmeOfficialAddress?.village),
    row("Block", msmeOfficialAddress?.block),
    row("City", msmeOfficialAddress?.city),
    row("District", msmeOfficialAddress?.district),
    row("State", msmeOfficialAddress?.state),
    row("Pincode", msmeOfficialAddress?.pincode),
    row("Mobile", msmeOfficialAddress?.mobile),
    row("Email", msmeOfficialAddress?.email),
  ]);

  pushSection("MSME Industry Details", "M", buildPdfTableSectionRows(msmeIndustryRows, [
    { key: "activity", label: "Activity" },
    { key: "industry", label: "Industry" },
    { key: "industryCode", label: "Industry Code" },
    { key: "subSector", label: "Sub Sector" },
    { key: "subSectorCode", label: "Sub Sector Code" },
    { key: "nicCode", label: "NIC Code" },
    { key: "activityDescription", label: "Description" },
  ], "Industry"));

  pushSection("Enterprise Classification History", "M", buildPdfTableSectionRows(msmeClassificationRows, [
    { key: "classificationYear", label: "Classification Year" },
    { key: "enterpriseType", label: "Enterprise Type" },
    { key: "classificationDate", label: "Classification Date" },
  ], "Classification"));

  pushSection("MSME Branch Details", "M", buildPdfTableSectionRows(msmeBranchRows, [
    { key: "name", label: "Name" },
    { key: "flat", label: "Flat" },
    { key: "premises", label: "Premises" },
    { key: "road", label: "Road" },
    { key: "village", label: "Village" },
    { key: "block", label: "Block" },
    { key: "city", label: "City" },
    { key: "district", label: "District" },
    { key: "state", label: "State" },
    { key: "pincode", label: "Pincode" },
  ], "Branch"));

  pushSection("Employment Details", "M", [
    row("Male", msmeEmploymentDetails?.male),
    row("Female", msmeEmploymentDetails?.female),
    row("Others", msmeEmploymentDetails?.others),
    row("Total", msmeEmploymentDetails?.total),
  ]);

  pushSection("MSME Bank Details", "M", [
    row("Bank", msmeBankDetails?.bank),
    row("Account No", msmeBankDetails?.acNo),
    row("IFSC", msmeBankDetails?.ifsc),
  ]);

  pushSection("MSME Financials", "M", [
    row("Financial Year", msmeFinancialRoot?.financialYear),
    row("Total Turnover", formatAmount(msmeFinancialDetails?.totalTurnover, true)),
    row("Export Turnover", formatAmount(msmeFinancialDetails?.exportTurnover, true)),
    row("Net Turnover", formatAmount(msmeFinancialDetails?.netTurnover, true)),
    row("WDV PME", formatAmount(msmeFinancialDetails?.wdvPme, true)),
    row("Net Investment In PME", formatAmount(msmeFinancialDetails?.netInvestmentInPme, true)),
    row("Exclusion", msmeFinancialDetails?.exclusion),
  ]);

  pushSection("Bank Details", "K", [
    row("Bank Name", firstValue(bank?.bank_name, payload?.bank_name, record?.bank_name)),
    row("Account Holder", firstValue(bank?.account_holder_name, payload?.account_holder_name, record?.account_holder_name)),
    row("Account No", firstValue(bank?.account_number, payload?.account_number, record?.account_number)),
    row("Account Type", firstValue(bank?.account_type, payload?.account_type, record?.account_type)),
    row("IFSC", firstValue(bank?.bank_identification_code, payload?.bank_identification_code, record?.bank_identification_code)),
    row("Branch Code", firstValue(bank?.branch_code, payload?.branch_code, record?.branch_code)),
    row("Branch Address", firstValue(bank?.branch_address, payload?.branch_address, record?.branch_address)),
    row("Bank Status", firstValue(bank?.bank_status, payload?.bank_status, record?.bank_status)),
  ]);

  pushSection("Bank Verification", "K", [
    row("Verified Account Name", bankSourceData?.accountName),
    row("Bank Response", bankSourceData?.bankResponse),
    row("Txn Status", bankSourceData?.bankTxnStatus),
    row("Valid", bankSource?.isValid),
    row("Source Status", bankSource?.statusAsPerSource),
    row("Bank RRN", firstValue(bankSourceData?.bankRRN, bankSourceData?.bankRrn, bankSourceData?.rrn)),
    row("Contact", firstValue(bankSourceData?.contact, bankSourceData?.phone, bankSourceData?.mobile)),
    row("Branch", firstValue(bankSourceData?.branch, ifscDetails?.BRANCH, ifscDetails?.branch)),
    row("State", firstValue(bankSourceData?.state, ifscDetails?.STATE, ifscDetails?.state)),
    row("City", firstValue(bankSourceData?.city, ifscDetails?.CITY, ifscDetails?.city)),
    row("District", firstValue(bankSourceData?.district, ifscDetails?.DISTRICT, ifscDetails?.district)),
    row("ISO3166", firstValue(bankSourceData?.iso3166, ifscDetails?.ISO3166, ifscDetails?.iso3166)),
    row("MICR", firstValue(bankSourceData?.micr, ifscDetails?.MICR, ifscDetails?.micr)),
    row("SWIFT", firstValue(bankSourceData?.swift, ifscDetails?.SWIFT, ifscDetails?.swift)),
    row("NEFT", firstValue(bankSourceData?.neft, ifscDetails?.NEFT, ifscDetails?.neft)),
    row("IMPS", firstValue(bankSourceData?.imps, ifscDetails?.IMPS, ifscDetails?.imps)),
    row("UPI", firstValue(bankSourceData?.upi, ifscDetails?.UPI, ifscDetails?.upi)),
    row("Centre", firstValue(bankSourceData?.centre, ifscDetails?.CENTRE, ifscDetails?.centre)),
    row("RTGS", firstValue(bankSourceData?.rtgs, ifscDetails?.RTGS, ifscDetails?.rtgs)),
    row("IFSC Bank", firstValue(ifscDetails?.BANK, ifscDetails?.bank)),
    row("IFSC Branch", firstValue(ifscDetails?.BRANCH, ifscDetails?.branch)),
  ]);

  pushSection("Company Details", "C", [
    row("Company Name", maskEntityName(firstValue(company?.company_name, record?.company_name, payload?.company_name))),
    row("Company Type", firstValue(company?.company_type, record?.company_type, payload?.company_type)),
    row("Official Email", firstValue(company?.company_official_email, record?.email, payload?.company_official_email)),
    row("Phone Number", firstValue(company?.company_phone_number, record?.mobile, payload?.company_phone_number)),
    row("Website", firstValue(company?.company_website, payload?.company_website)),
    row("Address", firstValue(company?.company_address, payload?.company_address)),
    row("City", firstValue(company?.city, payload?.city)),
    row("State", firstValue(company?.state, payload?.state)),
    row("Country", firstValue(company?.country, payload?.country, payload?.selected_country)),
    row("Postal Code", firstValue(company?.postal_code, payload?.postal_code)),
  ]);

  pushSection("Proprietor Contact Details", "C", [
    row("First Name", firstValue(proprietor?.first_name, proprietor?.firstName, proprietor?.name)),
    row("Last Name", firstValue(proprietor?.last_name, proprietor?.lastName)),
    row("Designation", proprietor?.designation),
    row("Country Code", firstValue(proprietor?.phone_country_code, proprietor?.country_code)),
    row("Official Mobile Number", firstValue(proprietor?.official_mobile_number, proprietor?.mobile_number, proprietor?.mobile, proprietor?.phone_number)),
    row("Official Email Address", firstValue(proprietor?.official_email_address, proprietor?.email_address, proprietor?.email)),
  ]);

  pushSection("Representative Contact Details", "C", [
    row("First Name", firstValue(representative?.first_name, representative?.firstName, representative?.name)),
    row("Last Name", firstValue(representative?.last_name, representative?.lastName)),
    row("Designation", representative?.designation),
    row("Country Code", firstValue(representative?.phone_country_code, representative?.country_code)),
    row("Official Mobile Number", firstValue(representative?.official_mobile_number, representative?.mobile_number, representative?.mobile, representative?.phone_number)),
    row("Official Email Address", firstValue(representative?.official_email_address, representative?.email_address, representative?.email)),
  ]);

  pushSection("Financial User Details", "C", [
    row("First Name", firstValue(financialUser?.first_name, financialUser?.firstName, financialUser?.name)),
    row("Last Name", firstValue(financialUser?.last_name, financialUser?.lastName)),
    row("Designation", financialUser?.designation),
    row("Country Code", firstValue(financialUser?.phone_country_code, financialUser?.country_code)),
    row("Official Mobile Number", firstValue(financialUser?.official_mobile_number, financialUser?.mobile_number, financialUser?.mobile, financialUser?.phone_number)),
    row("Official Email Address", firstValue(financialUser?.official_email_address, financialUser?.email_address, financialUser?.email)),
  ]);

  pushSection("Supplier Documents", "D", documentCards.map((document) =>
    row(document?.label, firstValue(document?.fileName, document?.fileType, document?.source ? "Available" : "")),
  ));

  return sections;
};

const buildUnifiedOnboardingPdfDocumentLinks = (record = {}) =>
  buildDocumentCards(record)
    .filter((document) => document?.source && !String(document.source).startsWith("data:"))
    .map((document) => ({
      label: document.label,
      url: document.source,
    }));


const maskPan = (value = "") => {
  const text = String(value || "").trim();
  if (!text || text === "-") return "-";
  if (text.length <= 4) return text;
  return `${text.slice(0, 4)}****${text.slice(-2)}`;
};

const maskListValues = (values = []) =>
  toArray(values)
    .map((value) => maskPan(value))
    .filter((value) => value && value !== "-")
    .join(", ");

const maskEntityName = (value = "") => {
  const text = String(value || "").trim();
  if (!text || text === "-") return "-";

  return text
    .split(/\s+/)
    .map((part) => {
      if (!part) return part;
      if (part.length <= 1) return part;
      return `${part[0]}${"*".repeat(Math.max(part.length - 1, 1))}`;
    })
    .join(" ");
};

const isAddressField = (label = "") => /address/i.test(String(label || ""));
const isReadMoreDisabledField = (label = "") =>
  isAddressField(label)
  || /nature of business|state jurisdiction|centre jurisdiction|activity class|activity group|activity sub class/i.test(String(label || ""));

const PlainTextValue = ({ value }) => {
  const text = displayValue(value);
  return (
    <span className={styles.plainTextValue} title={text}>
      {text}
    </span>
  );
};

const ReadMoreText = ({ value, maxLength = 95, disabled = false }) => {
  const [expanded, setExpanded] = useState(false);
  const text = displayValue(value);
  if (disabled) {
    return <PlainTextValue value={text} />;
  }
  const shouldToggle = text && text !== "-" && String(text).length > maxLength;

  if (!shouldToggle) {
    return (
      <span className={styles.singleLineValue} title={text}>
        {text}
      </span>
    );
  }

  return (
    <div className={styles.readMoreText}>
      {expanded ? (
        <span className={styles.readMoreExpandedText}>{text}</span>
      ) : (
        <span className={styles.singleLineValue} title={text}>
          {text}
        </span>
      )}
      <button
        type="button"
        className={styles.readMoreLink}
        onClick={() => setExpanded((current) => !current)}
      >
        {expanded ? "Read less" : "Read more"}
      </button>
    </div>
  );
};

const AddressPreview = ({ value, maxLength = 88 }) => (
  <ReadMoreText value={value} maxLength={maxLength} disabled />
);

const renderDetailValue = (label, value) => (
  <ReadMoreText
    value={value}
    maxLength={isAddressField(label) ? 88 : 110}
    disabled={isReadMoreDisabledField(label)}
  />
);

const DetailTable = ({ title, rows, columns, emptyText = "No data available.", collapseAfter = 3 }) => {
  const [expanded, setExpanded] = useState(false);
  const visibleRows = toArray(rows).filter((item) => isPlainObject(item) && Object.values(item).some((value) => displayValue(value) !== "-"));
  if (!visibleRows.length) return null;

  const shouldCollapse = collapseAfter > 0 && visibleRows.length > collapseAfter;
  const displayRows = shouldCollapse && !expanded ? visibleRows.slice(0, collapseAfter) : visibleRows;

  return (
    <div className={styles.groupBlock}>
      {title ? <h4>{title}</h4> : null}
      <div className={styles.miniTableWrap}>
        <table className={styles.miniTable}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} style={{ minWidth: column.minWidth, width: column.width }}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.length ? displayRows.map((item, index) => (
              <tr key={`${title || "table"}-${index}`}>
                {columns.map((column) => {
                  const rawValue = column.render ? column.render(item, index) : item?.[column.key];
                  const maxLength = column.maxLength || (isAddressField(column.label) || isAddressField(column.key) ? 88 : 95);
                  const disableReadMore = Boolean(column.disableReadMore) || isReadMoreDisabledField(column.label) || isReadMoreDisabledField(column.key);
                  const cellStyle = {
                    minWidth: column.minWidth,
                    width: column.width,
                  };
                  return (
                    <td key={column.key} title={displayValue(rawValue)} style={cellStyle}>
                      <ReadMoreText value={rawValue} maxLength={maxLength} disabled={disableReadMore} />
                    </td>
                  );
                })}
              </tr>
            )) : (
              <tr><td colSpan={columns.length}>{emptyText}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {shouldCollapse ? (
        <button type="button" className={styles.collapseToggle} onClick={() => setExpanded((value) => !value)}>
          {expanded ? "Show Less" : `Show ${visibleRows.length - collapseAfter} More`}
        </button>
      ) : null}
    </div>
  );
};

const GroupedCards = ({ title, items, getTitle, fields, collapseAfter = 3 }) => {
  const [expanded, setExpanded] = useState(false);
  const visibleItems = toArray(items).filter(Boolean);
  if (!visibleItems.length) return null;

  const shouldCollapse = collapseAfter > 0 && visibleItems.length > collapseAfter;
  const displayItems = shouldCollapse && !expanded ? visibleItems.slice(0, collapseAfter) : visibleItems;

  return (
    <div className={styles.groupBlock}>
      {title ? <h4>{title}</h4> : null}
      <div className={styles.groupedCards}>
        {displayItems.map((item, index) => (
          <div className={styles.groupCard} key={`${title || "card"}-${index}`}>
            <h5>{getTitle ? getTitle(item, index) : `${title || "Item"} ${index + 1}`}</h5>
            <div className={styles.groupCardGrid}>
              {cleanRows(fields(item, index)).map(([label, value]) => (
                <div key={`${label}-${value}`}>
                  <span>{label}</span>
                  <div className={styles.detailValue}>{renderDetailValue(label, value)}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {shouldCollapse ? (
        <button type="button" className={styles.collapseToggle} onClick={() => setExpanded((value) => !value)}>
          {expanded ? "Show Less" : `Show ${visibleItems.length - collapseAfter} More`}
        </button>
      ) : null}
    </div>
  );
};

const StatusChips = ({ title, values }) => {
  const chips = toArray(values).filter((value) => value !== null && value !== undefined && String(value).trim() !== "");
  if (!chips.length) return null;

  return (
    <div className={styles.groupBlock}>
      {title ? <h4>{title}</h4> : null}
      <div className={styles.chipList}>
        {chips.map((chip, index) => <span key={`${title || "chip"}-${index}`}>{displayValue(chip)}</span>)}
      </div>
    </div>
  );
};

const DetailSection = ({ title, rows, children, collapseAfter = 0 }) => {
  const [expanded, setExpanded] = useState(false);
  const visibleRows = rows?.filter(([, value]) => value && value !== "-") || [];
  const shouldCollapse = collapseAfter > 0 && visibleRows.length > collapseAfter;
  const displayRows = shouldCollapse && !expanded ? visibleRows.slice(0, collapseAfter) : visibleRows;
  if (!visibleRows.length && !children) return null;

  return (
    <div className={styles.detailSection}>
      <h3>{title}</h3>
      {displayRows.length ? (
        <div className={styles.detailGrid}>
          {displayRows.map(([label, value]) => (
            <div className={styles.detailItem} key={label}>
              <span>{label}</span>
              <div className={styles.detailValue}>{renderDetailValue(label, value)}</div>
            </div>
          ))}
        </div>
      ) : null}
      {shouldCollapse ? (
        <button type="button" className={styles.collapseToggle} onClick={() => setExpanded((value) => !value)}>
          {expanded ? "Show Less" : `Show ${visibleRows.length - collapseAfter} More`}
        </button>
      ) : null}
      {children}
    </div>
  );
};

const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index ? <Box>{children}</Box> : null}
  </div>
);

const UnifiedOnboarding = () => {
  const loginData = useSelector((state) => state.loginData.value);
  const [tabValue, setTabValue] = useState(0);
  const [type, setType] = useState("Supplier");
  const [mode, setMode] = useState("manual");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [amount, setAmount] = useState("");
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ error: false, text: "" });
  const [invitePopup, setInvitePopup] = useState({
    open: false,
    success: true,
    message: "",
  });
  const [masterRows, setMasterRows] = useState([]);
  const [masterLoading, setMasterLoading] = useState(false);
  const [masterError, setMasterError] = useState("");
  const [masterSearch, setMasterSearch] = useState("");
  const [masterOnboardingType, setMasterOnboardingType] = useState("All");
  const [masterSortOrder, setMasterSortOrder] = useState("desc");
  const [summaryCounts, setSummaryCounts] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const [masterPage, setMasterPage] = useState(1);
  const [selectedMasterRecord, setSelectedMasterRecord] = useState(null);
  const [detailTab, setDetailTab] = useState("gst");
  const [selectedDocumentSources, setSelectedDocumentSources] = useState([]);
  const [selectedDocumentViewer, setSelectedDocumentViewer] = useState(null);
  const [masterPdfLoadingKey, setMasterPdfLoadingKey] = useState("");
  const fileInputRef = useRef(null);

  const buyerToken = firstValue(
    loginData?.user?.user_token,
    loginData?.user?.token,
    loginData?.buyer_token,
    loginData?.token,
  );

  const orgToken = firstValue(
    loginData?.organization_token
  );
  const valid = mode === "bulk"
    ? Boolean(file)
    : name.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && /^\d{10}$/.test(mobile);

  const clear = () => {
    setName(""); setEmail(""); setMobile(""); setAmount(""); setFile(null);
  };

  const closeInvitePopup = () => {
    setInvitePopup((prev) => ({ ...prev, open: false }));
  };

  const fetchUnifiedOnboardingMaster = async () => {
    if (!loginData?.auth_token || !buyerToken) return;
    setMasterLoading(true);
    setMasterError("");

    try {
      const payload = {
        onboarding_type: masterOnboardingType,
        buyer_token: orgToken,
        page: 1,
        sort_key: "create_on",
        sort_order: masterSortOrder,
      };

      const response = await axios.post(
        API_ENDPOINT.get_unified_onboarding,
        payload,
        {
          headers: { Authorization: `Bearer ${loginData?.auth_token}` },
        },
      );

      const rows = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data?.response?.data)
          ? response.data.response.data
          : Array.isArray(response.data)
            ? response.data
            : [];

      setMasterRows(rows);
      setMasterPage(1);
    } catch (error) {
      setMasterError(String(error?.response?.data?.message || error?.response?.data?.error || "Unable to fetch unified onboarding records."));
      setMasterRows([]);
    } finally {
      setMasterLoading(false);
    }
  };

  const fetchUnifiedOnboardingSummaryCounts = async () => {
    if (!loginData?.auth_token || !buyerToken) return;
    setSummaryLoading(true);
    setSummaryError("");

    try {
      const response = await axios.post(
        API_ENDPOINT.unified_onboarding_summary_counts,
        {
          buyer_token: orgToken,
        },
        {
          headers: { Authorization: `Bearer ${loginData?.auth_token}` },
        },
      );

      setSummaryCounts(response.data?.response || response.data?.data || response.data || null);
    } catch (error) {
      setSummaryError(String(error?.response?.data?.message || error?.response?.data?.error || "Unable to fetch onboarding summary."));
      setSummaryCounts(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === 1) {
      fetchUnifiedOnboardingMaster();
      fetchUnifiedOnboardingSummaryCounts();
    }
  }, [tabValue, masterOnboardingType, masterSortOrder, loginData?.auth_token, buyerToken]);

  const filteredMasterRows = useMemo(() => {
    const search = masterSearch.trim().toLowerCase();
    if (!search) return masterRows;

    return masterRows.filter((record) => {
      const summary = getRecordSummary(record);
      return [
        summary.supplierName,
        summary.email,
        summary.phone,
        summary.companyName,
        summary.pan,
        summary.gstin,
        summary.cin,
        summary.bankName,
        summary.bankStatus,
        summary.msmeNo,
        summary.msmeStatus,
        summary.status,
        getOnboardingType(record),
        getUnifiedOnboardingStatus(record),
      ].some((value) => String(value || "").toLowerCase().includes(search));
    });
  }, [masterRows, masterSearch]);

  const totalMasterPages = Math.max(Math.ceil(filteredMasterRows.length / MASTER_ROWS_PER_PAGE), 1);
  const normalizedMasterPage = Math.min(Math.max(parseInt(masterPage, 10) || 1, 1), totalMasterPages);
  const paginatedMasterRows = filteredMasterRows.slice(
    (normalizedMasterPage - 1) * MASTER_ROWS_PER_PAGE,
    normalizedMasterPage * MASTER_ROWS_PER_PAGE,
  );

  const handleDownloadRecordPdf = async (record = {}) => {
    const recordKey = String(record?.id || record?.token || record?.vendor_token || record?.email || "");
    if (!recordKey) return;

    setMasterPdfLoadingKey(recordKey);

    try {
      const { default: jsPDF } = await import("jspdf");
      const autoTableModule = await import("jspdf-autotable");
      const autoTable = autoTableModule.default || autoTableModule.autoTable;

      const summary = getRecordSummary(record);
      summary.companyName = maskEntityName(summary.companyName);
      summary.supplierName = maskEntityName(summary.supplierName);
      const sections = buildUnifiedOnboardingPdfSections(record);
      const documentLinks = buildUnifiedOnboardingPdfDocumentLinks(record);
      const exportDate = new Date().toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margins = { top: 34, right: 12, bottom: 14, left: 12 };
      const contentWidth = pdfWidth - margins.left - margins.right;
      const labelColumnWidth = 58;
      const headingColor = [15, 23, 42];
      const sectionFillColor = [239, 246, 255];
      const borderColor = [226, 232, 240];
      const linkColor = [37, 99, 235];
      const headerTextX = margins.left + 31;
      const drawnHeaderPages = new Set();

      const renderPageHeader = () => {
        const pageNumber = pdf.internal.getCurrentPageInfo().pageNumber;
        if (drawnHeaderPages.has(pageNumber)) return;
        drawnHeaderPages.add(pageNumber);

        pdf.setFillColor(248, 250, 252);
        pdf.rect(0, 0, pdfWidth, 18, "F");
        pdf.addImage(bidzLogo, "PNG", margins.left, 4, 24, 8);

        pdf.setTextColor(15, 23, 42);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(pageNumber === 1 ? 15 : 13);
        pdf.text("Unified Onboarding Record", headerTextX, 8);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8.5);
        pdf.text("Downloaded from Buyer Unified Onboarding Master", headerTextX, 13);
        pdf.text(`Exported On: ${exportDate}`, pdfWidth - margins.right, 8, { align: "right" });

        pdf.setDrawColor(...borderColor);
        pdf.setLineWidth(0.2);
        pdf.line(margins.left, 22, pdfWidth - margins.right, 22);
      };

      const addPageWithHeader = () => {
        pdf.addPage();
        renderPageHeader();
      };

      const getLastAutoTableFinalY = () => pdf.lastAutoTable?.finalY ?? margins.top;

      const ensurePageSpace = (requiredHeight = 14) => {
        const currentY = getLastAutoTableFinalY();
        if (currentY + requiredHeight > pdfHeight - margins.bottom) {
          addPageWithHeader();
          return margins.top;
        }
        return currentY;
      };

      const renderSectionTable = (title, rows = [], startY) => {
        autoTable(pdf, {
          startY,
          margin: {
  top: margins.top,
  right: margins.right,
  bottom: margins.bottom,
  left: margins.left,
},
          tableWidth: contentWidth,
          theme: "grid",
          head: [[{
            content: title,
            colSpan: 2,
            styles: {
              fillColor: sectionFillColor,
              textColor: headingColor,
              fontStyle: "bold",
              fontSize: 11,
              halign: "left",
              cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
              lineColor: borderColor,
              lineWidth: 0.2,
            },
          }]],
          body: rows.map(([label, value]) => [displayValue(label), displayValue(value)]),
          styles: {
            font: "helvetica",
            fontSize: 9,
            textColor: headingColor,
            overflow: "linebreak",
            valign: "top",
            cellPadding: { top: 2.5, right: 2.5, bottom: 2.5, left: 2.5 },
            lineColor: borderColor,
            lineWidth: 0.15,
          },
          bodyStyles: {
            minCellHeight: 6,
          },
          columnStyles: {
            0: {
              cellWidth: labelColumnWidth,
              fontStyle: "bold",
            },
            1: {
              cellWidth: contentWidth - labelColumnWidth,
            },
          },
          rowPageBreak: "avoid",
          didDrawPage: renderPageHeader,
        });
      };

      renderPageHeader();

      renderSectionTable("Summary", [
        ["Company Name", firstValue(summary.companyName, summary.supplierName, "-")],
        ["Onboarding Type", getCleanLabel(getOnboardingType(record))],
        ["Onboarding Status", getUnifiedOnboardingStatus(record)],
      ], margins.top);

      sections.forEach((section) => {
        const nextY = ensurePageSpace(18) + 5;
        renderSectionTable(section.title, section.rows, nextY);
      });

      if (documentLinks.length) {
        autoTable(pdf, {
          startY: ensurePageSpace(18) + 5,
          margin: {
  top: margins.top,
  right: margins.right,
  bottom: margins.bottom,
  left: margins.left,
},
          tableWidth: contentWidth,
          theme: "grid",
          head: [[{
            content: "Document Links",
            colSpan: 2,
            styles: {
              fillColor: sectionFillColor,
              textColor: headingColor,
              fontStyle: "bold",
              fontSize: 11,
              halign: "left",
              cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
              lineColor: borderColor,
              lineWidth: 0.2,
            },
          }]],
          body: documentLinks.map((document) => [displayValue(document.label), displayValue(document.url)]),
          styles: {
            font: "helvetica",
            fontSize: 9,
            textColor: headingColor,
            overflow: "linebreak",
            valign: "top",
            cellPadding: { top: 2.5, right: 2.5, bottom: 2.5, left: 2.5 },
            lineColor: borderColor,
            lineWidth: 0.15,
          },
          columnStyles: {
            0: {
              cellWidth: labelColumnWidth,
              fontStyle: "bold",
            },
            1: {
              cellWidth: contentWidth - labelColumnWidth,
              textColor: linkColor,
            },
          },
          rowPageBreak: "avoid",
          didDrawPage: renderPageHeader,
          didDrawCell: (hookData) => {
            if (hookData.section !== "body" || hookData.column.index !== 1) return;
            const document = documentLinks[hookData.row.index];
            if (!document?.url) return;
            pdf.link(hookData.cell.x, hookData.cell.y, hookData.cell.width, hookData.cell.height, {
              url: document.url,
            });
          },
        });
      }

      const fileName = firstValue(record?.token, record?.id, summary.email, "unified-onboarding")
        .toString()
        .replace(/\s+/g, "_")
        .replace(/[^\w.-]/g, "")
        .toUpperCase();
      pdf.save(`unified-onboarding-${fileName || "record"}-${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Unified onboarding PDF export failed:", error);
      setInvitePopup({
        open: true,
        success: false,
        message: "Something went wrong while downloading the record. Please try again.",
      });
    } finally {
      setMasterPdfLoadingKey("");
    }
  };

  const handleFileChange = (event) => {
    setFile(event.target.files?.[0] || null);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    const nextFile = event.dataTransfer?.files?.[0] || null;
    setFile(nextFile);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!valid) return;
    const onboardingType = normalizeOnboardingType(type);
    const data = new FormData();
    data.append("buyer_token", loginData?.user?.token);
    data.append("vendorType", type);
    data.append("onboarding_type", onboardingType);
    data.append("onboarding_module", "unified");
    data.append("onboarding_route", "/unified_onboarding");
    if (mode === "bulk") data.append("vendor_file", file);
    else {
      data.append("vendor_name", name.trim());
      data.append("vendor_mail", email.trim());
      data.append("vendor_mobile", mobile);
      data.append("securityamount", amount.replace(/,/g, ""));
    }
    try {
      setLoading(true);

      const isManualSupplierInvite =
  mode === "manual" &&
  (type === "Supplier" ||
    type === "Customer" ||
    type === "Dealer" ||
    type === "Distributor" ||
    type === "Contractor");
      const endpoint = isManualSupplierInvite
        ? API_ENDPOINT.unified_dashboard_invite_vendor
        : API_ENDPOINT.invite_vendor;
      const requestData = isManualSupplierInvite
        ? {
            buyer_token: loginData?.user?.token,
            onboarding_type: onboardingType,
            invite_name: name.trim(),
            invite_email: email.trim(),
            invite_mobile: mobile,
            securityamount: Number(amount.replace(/,/g, "")) || 0,
          }
        : data;

      const response = await axios.post(endpoint, requestData, {
        headers: { Authorization: `Bearer ${loginData?.auth_token}` },
      });

      if (isManualSupplierInvite) {
        const invitedEmail = email.trim().toLowerCase();
        const successEmails = Array.isArray(response.data?.success)
          ? response.data.success.map((item) => String(item).trim().toLowerCase())
          : [];
        const failedEmails = Array.isArray(response.data?.failed)
          ? response.data.failed.map((item) => String(item).trim().toLowerCase())
          : [];
        const inviteSent = successEmails.includes(invitedEmail);
        const inviteFailed = failedEmails.includes(invitedEmail);

        setInvitePopup({
          open: true,
          success: inviteSent,
          message: inviteSent
            ? "Invite sent successfully."
            : inviteFailed
              ? "Invite failed."
              : "Invite failed.",
        });

        setFeedback({
          error: !inviteSent,
          text: inviteSent
            ? response.data?.message || "Invite sent successfully."
            : response.data?.message || "Invite failed.",
        });

        if (inviteSent) {
          clear();
        }
      } else {
        setFeedback({ error: false, text: response.data?.message || response.data?.response || `${type} onboarding initiated successfully.` });
        clear();
      }
    } catch (error) {
      if (mode === "manual" && type === "Supplier") {
        setInvitePopup({
          open: true,
          success: false,
          message: "Invite failed.",
        });
      }
      setFeedback({ error: true, text: String(error?.response?.data?.message || error?.response?.data?.error || "Unable to initiate onboarding.") });
    } finally { setLoading(false); }
  };

  const downloadSample = () => {
    const csv = `${type} Name,${type} Email,${type} Mobile,Security Amount\nExample,example@company.com,9999999999,10000`;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `sample_${type.toLowerCase().replace(/[^a-z0-9]+/g, "_")}_onboarding.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadDocument = (fileDocument) => {
    if (!fileDocument?.source) return;
    const link = window.document.createElement("a");
    link.href = fileDocument.source;
    link.target = "_blank";
    link.rel = "noreferrer";
    link.download = fileDocument.fileName || `${fileDocument.label || "document"}`;
    window.document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const toggleDocumentSelection = (source) => {
    if (!source) return;
    setSelectedDocumentSources((prev) =>
      prev.includes(source)
        ? prev.filter((item) => item !== source)
        : [...prev, source],
    );
  };

  const downloadSelectedDocuments = (documents = []) => {
    documents
      .filter((fileDocument) => fileDocument?.source && selectedDocumentSources.includes(fileDocument.source))
      .forEach(downloadDocument);
  };

  const requiredFieldSx  = {
  "& .MuiFormLabel-asterisk": {
    color: "red",
  },
};

  const renderInitiateOnboarding = () => (
    <>
      <Box className={styles.section}>
        <FormLabel className={styles.label}>Select the onboarding type</FormLabel>
        <RadioGroup row value={type} onChange={(e) => setType(e.target.value)} className={styles.types}>
          {ONBOARDING_TYPES.map(([value, description]) => (
            <Box className={styles.type} key={value}>
              <FormControlLabel value={value} control={<Radio size="small" />} label={<Typography className={styles.radioLabel}>{value}</Typography>} />
              <Tooltip title={description} arrow componentsProps={{ tooltip: { sx: { fontSize: "13px" } } }}><IconButton size="small" aria-label={`About ${value}`}><InfoOutlinedIcon fontSize="inherit" /></IconButton></Tooltip>
            </Box>
          ))}
        </RadioGroup>
      </Box>
      <Box className={styles.section}>
        <FormLabel className={styles.label}>Invite Mode</FormLabel>
        <RadioGroup row value={mode} onChange={(e) => { setMode(e.target.value); clear(); }}>
          <FormControlLabel value="manual" control={<Radio />} label={<Typography className={styles.inviteModeLabel}>Invite {type} manually</Typography>} />
          <FormControlLabel value="bulk" control={<Radio />} label={<Typography className={styles.inviteModeLabel}>Bulk {type} invite</Typography>} />
        </RadioGroup>
      </Box>
      <form onSubmit={submit}>
        {mode === "manual" ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}><TextField required sx={requiredFieldSx} className={styles.field} fullWidth label={`${type} Name`} value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 30 }} /></Grid>
            <Grid item xs={12} md={3}><TextField required sx={requiredFieldSx} className={styles.field} fullWidth type="email" label={`${type} Email`} value={email} onChange={(e) => setEmail(e.target.value)} /></Grid>
            <Grid item xs={12} md={3}><TextField required sx={requiredFieldSx} className={styles.field} fullWidth label={`${type} Mobile`} value={mobile} onChange={(e) => /^\d{0,10}$/.test(e.target.value) && setMobile(e.target.value)} /></Grid>
            <Grid item xs={12} md={3}><TextField className={styles.field} fullWidth label={`${type} Security Amount`} value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9,]/g, ""))} /></Grid>
          </Grid>
        ) : (
          <Box className={styles.bulk}>
            <Typography className={styles.label}>Bulk {type} Invite</Typography>
            <Box className={styles.bulkUploadBlock}>
              <div
                className={`${styles.fileUploadWrapper} ${dragging ? styles.drag_over : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className={styles.file_upload}>
                  {!file ? (
                    <>
                      <label className={styles.DocText}>
                        <span>Drag and drop file here</span>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".csv,.xlsx,.xls"
                          style={{ display: "none" }}
                          onChange={handleFileChange}
                        />
                        <p>(Supported format CSV/Excel)</p>
                      </label>
                      <button
                        type="button"
                        className={styles.browseButton}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Browse
                      </button>
                    </>
                  ) : (
                    <div className={styles.selectedFileRow}>
                      <span>
                        {file.name} ({(file.size / 1024).toFixed(2)} KB)
                      </span>
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => {
                          setFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </Box>
            <Button variant="outlined" onClick={downloadSample}>Download Sample CSV</Button>
          </Box>
        )}
        {feedback.text && <Typography className={feedback.error ? styles.error : styles.success}>{feedback.text}</Typography>}
        <Button className={styles.submit} type="submit" variant="contained" disabled={!valid || loading}>{loading ? <><CircularProgress size={16} color="inherit" />&nbsp; Sending...</> : "Send Invite"}</Button>
      </form>
    </>
  );

  const renderMasterDetails = () => {
    if (!selectedMasterRecord) return null;

    const payload = normalizeRecordPayload(selectedMasterRecord);
    const company = getCompanyDetails(selectedMasterRecord);
    const contacts = getContactDetails(selectedMasterRecord);
    const gst = payload?.gst_details || selectedMasterRecord?.gst_details || {};
    const pan = payload?.pan_details || selectedMasterRecord?.pan_details || {};
    const cin = payload?.cin_details || selectedMasterRecord?.cin_details || {};
    const msme = payload?.msme_details || selectedMasterRecord?.msme_details || {};
    const msmeResponse = payload?.msme_response || selectedMasterRecord?.msme_response || payload?.verification_responses?.msme || selectedMasterRecord?.verification_responses?.msme || {};
    const msmeLookup = firstObject(msmeResponse?.panLookup?.result?.[0], msmeResponse?.udyamLookup?.result?.[0], msmeResponse?.lookup?.result?.[0]);
    const msmeVerification = msmeResponse?.verification || {};
    const msmeResult = firstObject(msmeVerification?.result, msmeResponse?.result);
    const msmeProfile = firstObject(msmeResult?.profile);
    const msmeOfficialAddress = firstObject(msmeResult?.officialAddress);
    const msmeBankDetails = firstObject(msmeResult?.bankDetails);
    const msmeEmploymentDetails = firstObject(msmeResult?.employmentDetails);
    const msmeFinancialRoot = firstObject(msmeResult?.financials);
    const msmeFinancialDetails = firstObject(msmeFinancialRoot?.details, msmeFinancialRoot);
    const msmeIndustry = firstArray(msmeResult?.industry);
    const msmeEnterpriseTypes = firstArray(msmeResult?.enterpriseType);
    const msmeBranchDetails = firstArray(msmeResult?.branchDetails);
    const msmePdfData = firstValue(msmeResult?.pdfData, msmeResponse?.pdfData);
    const msmePdfDocument = msmePdfData
      ? {
          label: "MSME Certificate",
          fileName: `${firstValue(msmeResult?.udyamRegistrationNo, msme?.msme_registration_number, msmeLookup?.urn, "MSME_Certificate")}.pdf`,
          fileType: "application/pdf",
          source: `data:application/pdf;base64,${msmePdfData}`,
        }
      : null;
    const bank = payload?.bank_details || selectedMasterRecord?.bank_details || {};
    const documentCards = buildDocumentCards(selectedMasterRecord);

    const gstResponse = payload?.gst_response || selectedMasterRecord?.gst_response || payload?.verification_responses?.gst || selectedMasterRecord?.verification_responses?.gst || {};
    const gstVerification = gstResponse?.verification || {};
    const gstResult = firstObject(gstVerification?.result, payload?.verification_responses?.gst?.verification?.result);
    const gstProfile = firstObject(
      gstVerification?.gstProfile?.result?.[0],
      payload?.verification_responses?.gst?.verification?.gstProfile?.result?.[0],
    );

    const panResponse = payload?.pan_response || selectedMasterRecord?.pan_response || payload?.verification_responses?.pan || selectedMasterRecord?.verification_responses?.pan || {};
    const panProfile = firstObject(
      panResponse?.verification?.panProfile?.result,
      panResponse?.panProfile?.result,
      payload?.panProfile?.result,
      selectedMasterRecord?.panProfile?.result,
      panResponse?.verification?.result,
    );

    const cinResponse = payload?.cin_response || selectedMasterRecord?.cin_response || payload?.verification_responses?.cin || selectedMasterRecord?.verification_responses?.cin || {};
    const mcaResponse = payload?.mca_response || selectedMasterRecord?.mca_response || payload?.verification_responses?.mca || selectedMasterRecord?.verification_responses?.mca || {};
    const cinCompany = getVerificationResult(cinResponse, mcaResponse);
    const mcaVerificationMessage = firstValue(mcaResponse?.verification?.message, mcaResponse?.message);
    const combinedCinRiskFlags = {
      ...(cin?.cin_blacklist_flags || {}),
      isMlm: firstValue(cinResponse?.verification?.isMlm, cinCompany?.isMlm, cin?.cin_blacklist_flags?.isMlm),
      isPep: firstValue(cinResponse?.verification?.isPep, cinCompany?.isPep, cin?.cin_blacklist_flags?.isPep),
      isShell: firstValue(cinResponse?.verification?.isShell, cinCompany?.isShell, cin?.cin_blacklist_flags?.isShell),
      isFormerPep: firstValue(cinResponse?.verification?.isFormerPep, cinCompany?.isFormerPep, cin?.cin_blacklist_flags?.isFormerPep),
      isVanishing: firstValue(cinResponse?.verification?.isVanishing, cinCompany?.isVanishing, cin?.cin_blacklist_flags?.isVanishing),
      isNotFiled5inv: firstValue(cinResponse?.verification?.isNotFiled5inv, cinCompany?.isNotFiled5inv, cin?.cin_blacklist_flags?.isNotFiled5inv),
      isDomainInvalid_: firstValue(cinResponse?.verification?.isDomainInvalid_, cinCompany?.isDomainInvalid_, cin?.cin_blacklist_flags?.isDomainInvalid_),
      isCompanyUnderCirp: firstValue(cinResponse?.verification?.isCompanyUnderCirp, cinCompany?.isCompanyUnderCirp, cin?.cin_blacklist_flags?.isCompanyUnderCirp),
      isEmailDisposable_: firstValue(cinResponse?.verification?.isEmailDisposable_, cinCompany?.isEmailDisposable_, cin?.cin_blacklist_flags?.isEmailDisposable_),
      isCompanyStrikedOff: firstValue(cinResponse?.verification?.isCompanyStrikedOff, cinCompany?.isCompanyStrikedOff, cin?.cin_blacklist_flags?.isCompanyStrikedOff),
      isMcaCompanyDefaulter: firstValue(cinResponse?.verification?.isMcaCompanyDefaulter, cinCompany?.isMcaCompanyDefaulter, cin?.cin_blacklist_flags?.isMcaCompanyDefaulter),
      isCompanyUnderStrikeOff: firstValue(cinResponse?.verification?.isCompanyUnderStrikeOff, cinCompany?.isCompanyUnderStrikeOff, cin?.cin_blacklist_flags?.isCompanyUnderStrikeOff),
      isCompanyStrikedOffUs248: firstValue(cinResponse?.verification?.isCompanyStrikedOffUs248, cinCompany?.isCompanyStrikedOffUs248, cin?.cin_blacklist_flags?.isCompanyStrikedOffUs248),
      isCompanyUnderLiquidation: firstValue(cinResponse?.verification?.isCompanyUnderLiquidation, cinCompany?.isCompanyUnderLiquidation, cin?.cin_blacklist_flags?.isCompanyUnderLiquidation),
      isLlpStrikedOffUnderRule37: firstValue(cinResponse?.verification?.isLlpStrikedOffUnderRule37, cinCompany?.isLlpStrikedOffUnderRule37, cin?.cin_blacklist_flags?.isLlpStrikedOffUnderRule37),
      isSuspendedAtStockExchange: firstValue(cinResponse?.verification?.isSuspendedAtStockExchange, cinCompany?.isSuspendedAtStockExchange, cin?.cin_blacklist_flags?.isSuspendedAtStockExchange),
      isCompanyActiveNonCompliant: firstValue(cinResponse?.verification?.isCompanyActiveNonCompliant, cinCompany?.isCompanyActiveNonCompliant, cin?.cin_blacklist_flags?.isCompanyActiveNonCompliant),
      isAddressQualityPoor_: cinCompany?.isAddressQualityPoor_,
    };

    const bankResponse = payload?.bank_response || selectedMasterRecord?.bank_response || payload?.verification_responses?.bank || selectedMasterRecord?.verification_responses?.bank || {};
    const bankVerification = bankResponse?.verification || {};
    const bankSource = bankVerification?.result?.data?.source?.[0] || {};
    const bankSourceData = bankSource?.data || {};
    const ifscDetails = bankVerification?.ifscDetails || {};

    const detailTabs = [
      ["gst", "GST Details"],
      ["pan", "PAN Details"],
      ["cin", "Business Info"],
      ["msme", "MSME Details"],
      ["bank", "Bank Details"],
      ["contacts", "Contact Details"],
      ["documents", "Documents"],
    ];

    const renderFields = (rows = []) => {
      const visibleRows = cleanRows(rows);
      if (!visibleRows.length) return null;

      return (
        <div className={styles.detailGrid}>
          {visibleRows.map(([label, value]) => (
            <div className={styles.detailItem} key={`${label}-${value}`}>
              <span>{label}</span>
              <div className={styles.detailValue}>{renderDetailValue(label, value)}</div>
            </div>
          ))}
        </div>
      );
    };

    const renderSectionFromObject = (title, source) => {
      const rows = cleanRows(flattenFields(source));
      if (!rows.length) return null;
      return <DetailSection title={title} rows={rows} collapseAfter={3} />;
    };

    const renderContactSection = (title, source = {}) => {
      if (!source || typeof source !== "object") return null;

      const rows = [
        row("First Name", firstValue(source?.first_name, source?.firstName, source?.name)),
        row("Last Name", firstValue(source?.last_name, source?.lastName)),
        row("Designation", source?.designation),
        row("Country Code", firstValue(source?.phone_country_code, source?.country_code)),
        row("Official Mobile Number", firstValue(source?.official_mobile_number, source?.mobile_number, source?.mobile, source?.phone_number)),
        row("Official Email Address", firstValue(source?.official_email_address, source?.email_address, source?.email)),
      ];

      return cleanRows(rows).length ? (
        <DetailSection title={title} rows={rows} />
      ) : null;
    };

    const gstBranches = [
      ...toArray(gstResult?.adadr),
      ...toArray(gstProfile?.branches),
    ];

    const gstGoods = firstArray(gstProfile?.goodsDetails, gstResult?.goodsDetails);
    const gstServices = firstArray(gstProfile?.servicesDetails, gstResult?.servicesDetails);
    const gstSignatories = firstArray(gstProfile?.signatoriesContact).length
      ? gstProfile.signatoriesContact.map((item) => firstValue(item?.name, item?.email, item?.contact))
      : toArray(gstProfile?.signatories);

    return (
      <div className={styles.detailPage}>
        <div className={styles.detailPageTop}>
          <div>
            <h1>{displayValue(firstValue(selectedMasterRecord?.company_name, company?.company_name, selectedMasterRecord?.email))}</h1>
            <p>{displayValue(firstValue(selectedMasterRecord?.email, payload?.invite_email_address, company?.company_official_email))}</p>
          </div>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => {
              setSelectedMasterRecord(null);
              setDetailTab("gst");
              setSelectedDocumentSources([]);
              setSelectedDocumentViewer(null);
            }}
          >
            <ArrowLeftIcon sx={{ fontSize: "30px" }} />
            Back
          </button>
        </div>

        <div className={styles.detailTabs}>
          {detailTabs.map(([key, label]) => (
            <DetailsTab
              key={key}
              large={true}
              tabName={label}
              currentTab={detailTab === key}
              clickFn={() => setDetailTab(key)}
            />
          ))}
        </div>

        <div className={styles.detailTabScreen}>
          {detailTab === "gst" && (
            <>
              <DetailSection
                title="GST Details"
                rows={[
                  row("GST Number", firstValue(gst?.tax_identification_number, gstResult?.gstin, gstProfile?.gstin, selectedMasterRecord?.gstin)),
                  row("Trade Name", firstValue(gst?.gst_trade_name, gstResult?.tradeNam, gstProfile?.tradeName)),
                  row("Legal Name", firstValue(gst?.gst_legal_name, gstResult?.lgnm, gstProfile?.legalName)),
                  row("GST Place of Business", firstValue(gst?.gst_place_of_business, gstResult?.ctb, gstProfile?.constitutionOfBusiness)),
                  row("Nature of Business", firstValue(gst?.gst_nature_of_business, compactList(gstResult?.nba), compactList(gstProfile?.natureOfBusiness))),
                  row("Date of Liability", firstValue(gst?.gst_date_of_liability, gstResult?.rgdt, gstProfile?.dateOfRegistration)),
                  row("Date of Validity", firstValue(gst?.gst_date_of_validity, gstResult?.rgdt, gstProfile?.gstApprovalDate)),
                  row("Type of Registration", firstValue(gst?.gst_type_of_registration, gstResult?.dty, gstProfile?.dealerType)),
                  row("Cancellation Flag", firstValue(gst?.gst_cancellation_flag, gstResult?.canFlag, gstProfile?.cancellationFlag)),
                ]}
              />

              <DetailSection
                title="Principal Address"
                rows={[
                  row("Address", firstValue(gstResult?.pradr?.adr, gstProfile?.address)),
                  row("Nature of Business", firstValue(gstResult?.pradr?.ntr, gstProfile?.natureOfTrade)),
                  row("State", gstProfile?.state_),
                  row("Contact", gstProfile?.contact),
                  row("Email", gstProfile?.email),
                ]}
              />

              <DetailSection title="Additional Places / Branches">
                <GroupedCards
                  items={gstBranches}
                  getTitle={(item, index) => `Branch ${index + 1}`}
                  fields={(item) => [
                    row("Address", firstValue(item?.adr, item?.address)),
                    row("Nature", firstValue(item?.ntr, item?.natureOfTrade)),
                    row("Contact", item?.contact),
                    row("Email", item?.email),
                  ]}
                />
              </DetailSection>

              <DetailSection title="Goods / HSN Details">
                <DetailTable
                  rows={gstGoods}
                  columns={[
                    { key: "hsnCode", label: "HSN Code", render: (item) => item?.hsnCode },
                    { key: "chapterCode", label: "Chapter", render: (item) => item?.chapterCode },
                    { key: "description", label: "Description", maxLength: 115, render: (item) => firstValue(item?.hsn1, item?.hsn2, item?.chapter) },
                  ]}
                />
              </DetailSection>

              <DetailSection title="Services / SAC Details">
                <DetailTable
                  rows={gstServices}
                  columns={[
                    { key: "sacCode", label: "SAC Code", render: (item) => item?.sacCode },
                    { key: "service", label: "Service", render: (item) => firstValue(item?.sac1, item?.sac2) },
                  ]}
                />
              </DetailSection>

              <StatusChips title="Signatories" values={gstSignatories} />

              <DetailSection title="GST Compliance">
                {renderFields([
                  row("PAN Profile Status", panProfile?.status),
                  row("Aggregate Turnover", firstValue(gstProfile?.aggregateTurnover, gstProfile?.aggregateTurnovers?.[0]?.turnover)),
                  row("Aadhaar Auth", gstProfile?.aadhaarAuthenticated),
                  row("Last Updated", firstValue(gstResult?.lstupdt, gstProfile?.lastUpdatedDate)),
                  row("State Jurisdiction", gstProfile?.stateJurisdiction),
                  row("Centre Jurisdiction", gstProfile?.centreJurisdiction),
                ])}
                <DetailTable
                  title="Turnover by FY"
                  rows={gstProfile?.aggregateTurnovers}
                  columns={[
                    { key: "financialYear", label: "FY" },
                    { key: "turnover", label: "Turnover" },
                  ]}
                />
                <DetailTable
                  title="Filing Frequency"
                  rows={gstProfile?.filingFrequencies}
                  columns={[
                    { key: "financialYear", label: "FY" },
                    { key: "frequency", label: "Frequency", render: (item) => toArray(item?.frequencies).map((fq) => `${fq?.quarter}: ${fq?.preference}`).join(", ") },
                  ]}
                />
              </DetailSection>
            </>
          )}

          {detailTab === "pan" && (
            <>
              <DetailSection
                title="PAN Details"
                rows={[
                  row("PAN Number", firstValue(pan?.pan_number, payload?.pan_number, selectedMasterRecord?.pan_number, selectedMasterRecord?.pan)),
                  row("PAN Name", firstValue(pan?.pan_name, payload?.pan_name, selectedMasterRecord?.pan_name, panProfile?.name)),
                  row("PAN Status", firstValue(pan?.pan_status, payload?.pan_status, selectedMasterRecord?.pan_status, panProfile?.status)),
                  row("PAN Profile Status", panProfile?.status),
                  row("Date of Birth", panProfile?.dob),
                ]}
              />
            </>
          )}

          {detailTab === "cin" && (
            <>
              <DetailSection
                title="Business / CIN Details"
                rows={[
                  row("CIN", firstValue(cin?.company_registration_number, cin?.cin_number, payload?.company_registration_number, selectedMasterRecord?.company_registration_number, cinCompany?.entityId, selectedMasterRecord?.cin)),
                  row("CIN Status", firstValue(cin?.cin_status, payload?.cin_status, selectedMasterRecord?.cin_status, cinCompany?.status)),
                  row("MCA Status", cinCompany?.status),
                  row("Company Name", firstValue(cinCompany?.name, selectedMasterRecord?.company_name, company?.company_name)),
                  row("MCA Email", cinCompany?.email),
                  row("Associated PAN", maskListValues(cinCompany?.pans)),
                  row("Class", cinCompany?.class),
                  row("Category", cinCompany?.category),
                  row("Subcategory", cinCompany?.subcategory),
                  row("Company Type", cinCompany?.type_),
                  row("Registration No", cinCompany?.registrationNumber),
                  row("MCA Constitution Code", cinCompany?.mcaConstitutionCode_),
                  row("Industry", cinCompany?.industry_),
                  row("Sub Industry", cinCompany?.subIndustry_),
                  row("Email Domain", cinCompany?.emailDomain),
                  row("Country", cinCompany?.countryOfIncorporation),
                  row("ROC", cinCompany?.rocCode),
                  row("Incorporated On", cinCompany?.dateOfIncorporation),
                  row("Last AGM", cinCompany?.dateOfLastAgm),
                  row("Balance Sheet Date", cinCompany?.dateOfBalanceSheet),
                  row("Annual Return Filed", cinCompany?.dateOfAnnualReturnFiled),
                  row("Listing Status", cinCompany?.listingStatus),
                  row("Compliance", cinCompany?.activeCompliance),
                  row("Authorised Capital", formatAmount(cinCompany?.authorisedCapital)),
                  row("Paid-up Capital", formatAmount(cinCompany?.paidUpCapital)),
                  row("Members", cinCompany?.numberOfMembers),
                  row("Partners", cinCompany?.numberOfPartners),
                  row("Designated Partners", cinCompany?.numberOfDesignatedPartners),
                  row("Registered Address", cinCompany?.registeredAddress),
                  row("Activity Class", cinCompany?.activityClass_),
                  row("Activity Group", cinCompany?.activityGroup_),
                  row("Activity Sub Class", cinCompany?.activitySubClass_),
                ]}
              />

              <DetailSection title="Directors">
                <DetailTable
                  rows={cinCompany?.directors}
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "designation", label: "Designation" },
                    { key: "din", label: "DIN" },
                    { key: "pan", label: "PAN", render: (item) => maskPan(item?.pan) },
                    { key: "dateOfAppointment", label: "Appointed On" },
                    { key: "tenureBeginDate", label: "Tenure Start" },
                    { key: "tenureEndDate", label: "Tenure End" },
                  ]}
                />
              </DetailSection>

              <DetailSection title="Former Directors">
                <DetailTable
                  rows={cinCompany?.formerDirectors}
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "designation", label: "Designation" },
                    { key: "din", label: "DIN" },
                    { key: "pan", label: "PAN", render: (item) => maskPan(item?.pan) },
                    { key: "tenureBeginDate", label: "Start" },
                    { key: "tenureEndDate", label: "End" },
                  ]}
                />
              </DetailSection>

              <DetailSection title="Charges">
                <DetailTable
                  rows={cinCompany?.charges}
                  columns={[
                    { key: "chargeHolderName", label: "Holder", minWidth: "18rem" },
                    { key: "chargeHolderNameStandardized_", label: "Std. Holder", minWidth: "18rem" },
                    { key: "amount", label: "Amount", minWidth: "12rem", render: (item) => formatAmount(item?.amount, true) },
                    { key: "status", label: "Status" },
                    { key: "srn", label: "SRN" },
                    { key: "chargeId", label: "Charge ID" },
                    { key: "chargeHolderTag_", label: "Holder Type" },
                    { key: "dateOfCreation", label: "Created On" },
                    { key: "dateOfModification", label: "Modified On" },
                    { key: "dateOfSatisfaction", label: "Satisfied On" },
                    { key: "address", label: "Address", minWidth: "28rem", disableReadMore: true },
                  ]}
                />
              </DetailSection>

              <DetailSection
                title="Risk / Compliance Checks"
                rows={cleanRows(flattenFields(combinedCinRiskFlags))}
                collapseAfter={3}
              />
              {renderSectionFromObject("Financials", cin?.corp_financials)}
            </>
          )}

          {detailTab === "msme" && (
            <>
              <DetailSection
                title="MSME Details"
                rows={[
                  row("MSME No", firstValue(msme?.msme_registration_number, payload?.msme_registration_number, selectedMasterRecord?.msme_registration_number, msmeResult?.udyamRegistrationNo, msmeLookup?.urn)),
                  row("Enterprise Name", firstValue(msme?.msme_name_of_enterprise, payload?.msme_name_of_enterprise, selectedMasterRecord?.msme_name_of_enterprise, msmeProfile?.name, msmeLookup?.name)),
                  row("Enterprise Type", firstValue(msme?.msme_type_of_enterprise, payload?.msme_type_of_enterprise, selectedMasterRecord?.msme_type_of_enterprise, msmeEnterpriseTypes?.[0]?.enterpriseType)),
                  row("Major Activity", firstValue(msme?.msme_major_activity, payload?.msme_major_activity, selectedMasterRecord?.msme_major_activity, msmeProfile?.majorActivity)),
                  row("Date of Filing", firstValue(msme?.msme_date_of_filing, payload?.msme_date_of_filing, selectedMasterRecord?.msme_date_of_filing, msmeResult?.dateOfRegistration)),
                  row("Date of Registration", msmeResult?.dateOfRegistration),
                  row("MSME Status", firstValue(msme?.msme_status, payload?.msme_status, selectedMasterRecord?.msme_status)),
                  row("Organization Type", msmeProfile?.organizationType),
                  row("Social Category", msmeProfile?.socialCategory),
                  row("Specially Abled", msmeProfile?.speciallyAbled),
                  row("Date of Commencement", msmeProfile?.dateOfCommencement),
                  row("Date of Incorporation", msmeProfile?.dateOfIncorporation),
                  row("Owner Name", msmeProfile?.ownerName),
                  row("Gender", msmeProfile?.gender),
                  row("GSTN Status", msmeProfile?.gstnStatus),
                  row("ITR Type", msmeProfile?.itrType),
                  row("Previous ITR Status", msmeProfile?.previousITRStatus),
                  row("Interested In GeM", msmeResult?.interestedInGem),
                  row("Interested In TReDS", msmeResult?.interestedInTreds),
                ]}
              />

              {msmePdfDocument ? (
                <DetailSection
                  title="MSME Certificate"
                  rows={[
                    row("Certificate PDF", "Available"),
                    row("Source", "MSME Verification Response"),
                  ]}
                >
                  <div className={styles.documentActions}>
                    <button type="button" onClick={() => setSelectedDocumentViewer(msmePdfDocument)}>
                      View Certificate
                    </button>
                    <button type="button" onClick={() => downloadDocument(msmePdfDocument)}>
                      Download Certificate
                    </button>
                  </div>
                </DetailSection>
              ) : null}

              <DetailSection
                title="MSME Official Address"
                rows={[
                  row("Flat / Door No", msmeOfficialAddress?.flat),
                  row("Premises", msmeOfficialAddress?.premises),
                  row("Road", msmeOfficialAddress?.road),
                  row("Village", msmeOfficialAddress?.village),
                  row("Block", msmeOfficialAddress?.block),
                  row("City", msmeOfficialAddress?.city),
                  row("District", msmeOfficialAddress?.district),
                  row("State", msmeOfficialAddress?.state),
                  row("Pincode", msmeOfficialAddress?.pincode),
                  row("Mobile", msmeOfficialAddress?.mobile),
                  row("Email", msmeOfficialAddress?.email),
                ]}
              />

              <DetailSection title="MSME Industry Details">
                <DetailTable
                  rows={msmeIndustry}
                  columns={[
                    { key: "activity", label: "Activity" },
                    { key: "industry", label: "Industry" },
                    { key: "industryCode", label: "Industry Code" },
                    { key: "subSector", label: "Sub Sector" },
                    { key: "subSectorCode", label: "Sub Sector Code" },
                    { key: "nicCode", label: "NIC Code" },
                    { key: "activityDescription", label: "Description", maxLength: 70 },
                  ]}
                />
              </DetailSection>

              <DetailSection title="Enterprise Classification History">
                <DetailTable
                  rows={msmeEnterpriseTypes}
                  columns={[
                    { key: "classificationYear", label: "Classification Year" },
                    { key: "enterpriseType", label: "Enterprise Type" },
                    { key: "classificationDate", label: "Classification Date" },
                  ]}
                />
              </DetailSection>

              <DetailSection title="MSME Branch Details">
                <DetailTable
                  rows={msmeBranchDetails}
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "flat", label: "Flat" },
                    { key: "premises", label: "Premises" },
                    { key: "road", label: "Road" },
                    { key: "village", label: "Village" },
                    { key: "block", label: "Block" },
                    { key: "city", label: "City" },
                    { key: "district", label: "District" },
                    { key: "state", label: "State" },
                    { key: "pincode", label: "Pincode" },
                  ]}
                />
              </DetailSection>

              <DetailSection
                title="Employment Details"
                rows={[
                  row("Male", msmeEmploymentDetails?.male),
                  row("Female", msmeEmploymentDetails?.female),
                  row("Others", msmeEmploymentDetails?.others),
                  row("Total", msmeEmploymentDetails?.total),
                ]}
              />

              <DetailSection
                title="MSME Bank Details"
                rows={[
                  row("Bank", msmeBankDetails?.bank),
                  row("Account No", msmeBankDetails?.acNo),
                  row("IFSC", msmeBankDetails?.ifsc),
                ]}
              />

              <DetailSection
                title="MSME Financials"
                rows={[
                  row("Financial Year", msmeFinancialRoot?.financialYear),
                  row("Total Turnover", formatAmount(msmeFinancialDetails?.totalTurnover, true)),
                  row("Export Turnover", formatAmount(msmeFinancialDetails?.exportTurnover, true)),
                  row("Net Turnover", formatAmount(msmeFinancialDetails?.netTurnover, true)),
                  row("WDV PME", formatAmount(msmeFinancialDetails?.wdvPme, true)),
                  row("Net Investment In PME", formatAmount(msmeFinancialDetails?.netInvestmentInPme, true)),
                  row("Exclusion", msmeFinancialDetails?.exclusion),
                ]}
              />
            </>
          )}

          {detailTab === "bank" && (
            <>
              <DetailSection
                title="Bank Details"
                rows={[
                  row("Bank Name", firstValue(bank?.bank_name, payload?.bank_name, selectedMasterRecord?.bank_name)),
                  row("Account Holder", firstValue(bank?.account_holder_name, payload?.account_holder_name, selectedMasterRecord?.account_holder_name)),
                  row("Account No", firstValue(bank?.account_number, payload?.account_number, selectedMasterRecord?.account_number)),
                  row("Account Type", firstValue(bank?.account_type, payload?.account_type, selectedMasterRecord?.account_type)),
                  row("IFSC", firstValue(bank?.bank_identification_code, payload?.bank_identification_code, selectedMasterRecord?.bank_identification_code)),
                  row("Branch Code", firstValue(bank?.branch_code, payload?.branch_code, selectedMasterRecord?.branch_code)),
                  row("Branch Address", firstValue(bank?.branch_address, payload?.branch_address, selectedMasterRecord?.branch_address)),
                  row("Bank Status", firstValue(bank?.bank_status, payload?.bank_status, selectedMasterRecord?.bank_status)),
                ]}
              />

              <DetailSection
                title="Bank Verification"
                rows={[
                  row("Verified Account Name", bankSourceData?.accountName),
                  row("Bank Response", bankSourceData?.bankResponse),
                  row("Txn Status", bankSourceData?.bankTxnStatus),
                  row("Valid", bankSource?.isValid),
                  row("Source Status", bankSource?.statusAsPerSource),
                  row("Bank RRN", firstValue(bankSourceData?.bankRRN, bankSourceData?.bankRrn, bankSourceData?.rrn)),
                  row("Contact", firstValue(bankSourceData?.contact, bankSourceData?.phone, bankSourceData?.mobile)),
                  row("Branch", firstValue(bankSourceData?.branch, ifscDetails?.BRANCH, ifscDetails?.branch)),
                  row("State", firstValue(bankSourceData?.state, ifscDetails?.STATE, ifscDetails?.state)),
                  row("City", firstValue(bankSourceData?.city, ifscDetails?.CITY, ifscDetails?.city)),
                  row("District", firstValue(bankSourceData?.district, ifscDetails?.DISTRICT, ifscDetails?.district)),
                  row("ISO3166", firstValue(bankSourceData?.iso3166, ifscDetails?.ISO3166, ifscDetails?.iso3166)),
                  row("MICR", firstValue(bankSourceData?.micr, ifscDetails?.MICR, ifscDetails?.micr)),
                  row("SWIFT", firstValue(bankSourceData?.swift, ifscDetails?.SWIFT, ifscDetails?.swift)),
                  row("NEFT", firstValue(bankSourceData?.neft, ifscDetails?.NEFT, ifscDetails?.neft)),
                  row("IMPS", firstValue(bankSourceData?.imps, ifscDetails?.IMPS, ifscDetails?.imps)),
                  row("UPI", firstValue(bankSourceData?.upi, ifscDetails?.UPI, ifscDetails?.upi)),
                  row("Centre", firstValue(bankSourceData?.centre, ifscDetails?.CENTRE, ifscDetails?.centre)),
                  row("RTGS", firstValue(bankSourceData?.rtgs, ifscDetails?.RTGS, ifscDetails?.rtgs)),
                  row("IFSC Bank", firstValue(ifscDetails?.BANK, ifscDetails?.bank)),
                  row("IFSC Branch", firstValue(ifscDetails?.BRANCH, ifscDetails?.branch)),
                ]}
              />
            </>
          )}

          {detailTab === "contacts" && (
            <>
              <DetailSection
                title="Company Details"
                rows={[
                  row("Company Name", firstValue(company?.company_name, selectedMasterRecord?.company_name, payload?.company_name)),
                  row("Company Type", firstValue(company?.company_type, selectedMasterRecord?.company_type, payload?.company_type)),
                  row("Official Email", firstValue(company?.company_official_email, selectedMasterRecord?.email, payload?.company_official_email)),
                  row("Phone Number", firstValue(company?.company_phone_number, selectedMasterRecord?.mobile, payload?.company_phone_number)),
                  row("Website", firstValue(company?.company_website, payload?.company_website)),
                  row("Address", firstValue(company?.company_address, payload?.company_address)),
                  row("City", firstValue(company?.city, payload?.city)),
                  row("State", firstValue(company?.state, payload?.state)),
                  row("Country", firstValue(company?.country, payload?.country, payload?.selected_country)),
                  row("Postal Code", firstValue(company?.postal_code, payload?.postal_code)),
                ]}
              />

              {renderContactSection("Proprietor Contact Details", contacts?.proprietor)}
              {renderContactSection("Representative Contact Details", contacts?.representative)}
              {renderContactSection("Financial User Details", contacts?.financial_user || contacts?.finance_representative)}
            </>
          )}

          {detailTab === "documents" && (
            <DetailSection title="Supplier Documents">
              {documentCards.length ? (
                <>
                  <div className={styles.documentActions}>
                    <button
                      type="button"
                      disabled={!selectedDocumentSources.length}
                      onClick={() => downloadSelectedDocuments(documentCards)}
                    >
                      Download Selected
                    </button>
                  </div>
                  <div className={styles.documentViewGrid}>
                    {documentCards.map((fileDocument, index) => {
                      const documentType = getDocumentType(fileDocument);
                      const isImage = documentType === "image";
                      const isPdf = documentType === "pdf";

                      return (
                        <div className={styles.documentViewCard} key={`${fileDocument.label}-${index}`}>
                          <label className={styles.documentCheckbox}>
                            <input
                              type="checkbox"
                              checked={fileDocument.source ? selectedDocumentSources.includes(fileDocument.source) : false}
                              disabled={!fileDocument.source}
                              onChange={() => toggleDocumentSelection(fileDocument.source)}
                            />
                          </label>
                          <div className={styles.documentPreviewBox}>
                            {fileDocument.source && isImage ? (
                              <img src={fileDocument.source} alt={fileDocument.label} />
                            ) : fileDocument.source ? (
                              <div className={styles.documentFilePreview}>
                                <InsertDriveFileOutlinedIcon sx={{ fontSize: "42px" }} />
                                <span>{isPdf ? "PDF" : documentType.toUpperCase()}</span>
                              </div>
                            ) : (
                              <div className={styles.documentPlaceholder}>
                                <InsertDriveFileOutlinedIcon sx={{ fontSize: "42px" }} />
                                <strong>NO IMAGE</strong>
                                <span>AVAILABLE</span>
                              </div>
                            )}

                            <div className={styles.documentIconActions}>
                              <Tooltip title={fileDocument.source ? "View" : "Document preview not available"} arrow>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!fileDocument.source}
                                    onClick={() => setSelectedDocumentViewer(fileDocument)}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={fileDocument.source ? "Download" : "Download not available"} arrow>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!fileDocument.source}
                                    onClick={() => downloadDocument(fileDocument)}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </div>
                          </div>
                          <div className={styles.documentLabel}>{fileDocument.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p className={styles.emptyText}>No documents available.</p>
              )}
            </DetailSection>
          )}
        </div>
      </div>
    );
  };

  const renderMasterTableCell = (value, className = "") => {
    const text = displayValue(value);
    return (
      <Tooltip title={text} arrow componentsProps={TOOLTIP_PROPS}>
        <span className={`${styles.tableText} ${className}`}>{text}</span>
      </Tooltip>
    );
  };

  const renderSummaryCards = () => {
    const total = Number(summaryCounts?.total_count || 0);
    const percentage = (value) => {
      if (!total) return "0.0%";
      return `${((Number(value || 0) / total) * 100).toFixed(1)}%`;
    };
    const cards = [
      { label: "Total Records", value: summaryCounts?.total_count, percent: "100%", helper: "Across all types", tone: "blue" },
      { label: "Fully Onboarded", value: summaryCounts?.fully_onboarded_count, percent: percentage(summaryCounts?.fully_onboarded_count), helper: "Required checks completed", tone: "green" },
      { label: "Partially Onboarded", value: summaryCounts?.partially_onboarded_count, percent: percentage(summaryCounts?.partially_onboarded_count), helper: "Pending required details", tone: "orange" },
      { label: "Suppliers", value: summaryCounts?.supplier_count, percent: percentage(summaryCounts?.supplier_count), helper: "Supplier records", tone: "blue" },
      { label: "Customers", value: summaryCounts?.customer_count, percent: percentage(summaryCounts?.customer_count), helper: "Customer records", tone: "green" },
      { label: "Dealers", value: summaryCounts?.dealer_count, percent: percentage(summaryCounts?.dealer_count), helper: "Dealer records", tone: "orange" },
      { label: "Distributors", value: summaryCounts?.distributor_count, percent: percentage(summaryCounts?.distributor_count), helper: "Distributor records", tone: "blue" },
      { label: "Contractors", value: summaryCounts?.contractor_count, percent: percentage(summaryCounts?.contractor_count), helper: "Contractor records", tone: "red" },
    ];

    return (
      <div className={styles.kpiGrid}>
        {summaryLoading ? (
          <div className={styles.kpiState}><CircularProgress size={18} /> Loading summary...</div>
        ) : summaryError ? (
          <div className={styles.kpiError}>{summaryError}</div>
        ) : (
          cards.map((card) => (
            <div className={`${styles.kpiCard} ${styles[`kpi${card.tone}`] || ""}`} key={card.label}>
              {/* <div className={styles.kpiIcon}>
                <InfoOutlinedIcon sx={{ fontSize: "18px" }} />
              </div> */}
              <div className={styles.kpiValueRow}>
                <strong>{displayValue(card.value || 0)}</strong>
                <span>{card.percent}</span>
              </div>
              <p>{card.label}</p>
              <small>{card.helper}</small>
            </div>
          ))
        )}
      </div>
    );
  };

  const renderUnifiedMaster = () => (
    <div className={styles.masterWrapper}>
      {selectedMasterRecord ? (
        renderMasterDetails()
      ) : (
        <>
      <div className={styles.masterHero}>
        <div>
          <h1>Unified Onboarding Master</h1>
          <p>Unified onboarding records across suppliers, customers, dealers, distributors, and contractors.</p>
        </div>
        <button
          type="button"
          onClick={() => {
            fetchUnifiedOnboardingMaster();
            fetchUnifiedOnboardingSummaryCounts();
          }}
          disabled={masterLoading}
        >
          {masterLoading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {renderSummaryCards()}

      <div className={styles.masterToolbar}>
        <div className={styles.masterSearch}>
          <SearchBar
            value={masterSearch}
            onChange={(query) => {
              setMasterSearch(query);
              setMasterPage(1);
            }}
          />
        </div>
        <label className={styles.statusFilter}>
          Onboarding Type
          <select
            value={masterOnboardingType}
            onChange={(event) => {
              setMasterOnboardingType(event.target.value);
              setMasterPage(1);
            }}
          >
            {MASTER_ONBOARDING_TYPES.map((option) => (
              <option value={option} key={option}>{getCleanLabel(option)}</option>
            ))}
          </select>
        </label>
      </div>

      <div className={styles.tablePanel}>
        <div className={styles.tablePanelHeader}>
          <span>
            Showing <strong>{paginatedMasterRows.length}</strong> of <strong>{filteredMasterRows.length}</strong> records
          </span>
        </div>
        <div className={styles.tableScroll}>
          <table className={styles.masterTable}>
            <thead>
              <tr>
                <th>Company</th>
                <th>Type</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>PAN</th>
                <th>GST</th>
                <th>CIN</th>
                <th>Bank</th>
                <th>MSME</th>
                <th>Onboarding Status</th>
                <th>
                  <button
                    type="button"
                    className={styles.sortHeaderButton}
                    onClick={() => setMasterSortOrder((value) => (value === "asc" ? "desc" : "asc"))}
                  >
                    Created {masterSortOrder === "asc" ? "↑" : "↓"}
                  </button>
                </th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {masterLoading ? (
                <tr>
                  <td colSpan={12} className={styles.stateCell}>
                    <CircularProgress size={18} /> Loading unified onboarding records...
                  </td>
                </tr>
              ) : masterError ? (
                <tr>
                  <td colSpan={12} className={styles.errorCell}>{masterError}</td>
                </tr>
              ) : paginatedMasterRows.length === 0 ? (
                <tr>
                  <td colSpan={12} className={styles.stateCell}>No unified onboarding records found.</td>
                </tr>
              ) : (
                paginatedMasterRows.map((record) => {
                  const summary = getRecordSummary(record);
                  const onboardingType = getCleanLabel(getOnboardingType(record));
                  const onboardingStatus = getUnifiedOnboardingStatus(record);
                  const recordKey = String(record?.id || record?.token || record?.vendor_token || summary.email);
                  const isDownloadingPdf = masterPdfLoadingKey === recordKey;
                  return (
                    <tr key={record?.id || record?.token || record?.vendor_token || summary.email}>
                      <td>{renderMasterTableCell(firstValue(summary.companyName, summary.supplierName), styles.tableCompany)}</td>
                      <td>{renderMasterTableCell(onboardingType)}</td>
                      <td>{renderMasterTableCell(summary.email)}</td>
                      <td>{renderMasterTableCell(summary.phone)}</td>
                      <td>{renderMasterTableCell(summary.pan)}</td>
                      <td>{renderMasterTableCell(summary.gstin)}</td>
                      <td>{renderMasterTableCell(summary.cin)}</td>
                      <td>{renderMasterTableCell(firstValue(summary.bankName, summary.bankStatus))}</td>
                      <td>{renderMasterTableCell(firstValue(summary.msmeNo, summary.msmeStatus))}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${onboardingStatus === "Fully Onboarded" ? styles.statusFull : styles.statusPartial}`}>
                          {onboardingStatus}
                        </span>
                      </td>
                      <td>{renderMasterTableCell(formatDate(summary.createdDate))}</td>
                      <td>
                        <div className={styles.actionButtons}>
                        <Tooltip title="View details" arrow componentsProps={TOOLTIP_PROPS}>
                          <IconButton
                            size="small"
                            className={styles.viewAction}
                            onClick={() => {
                              setSelectedMasterRecord(record);
                              setDetailTab("gst");
                              setSelectedDocumentSources([]);
                            }}
                          >
                            <VisibilityIcon fontSize="medium" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download record" arrow componentsProps={TOOLTIP_PROPS}>
                          <IconButton
                            size="small"
                            className={styles.downloadAction}
                            disabled={isDownloadingPdf}
                            onClick={() => handleDownloadRecordPdf(record)}
                          >
                            {isDownloadingPdf ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon fontSize="medium" />}
                          </IconButton>
                        </Tooltip>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <PaginationComponent
        pageNumber={normalizedMasterPage}
        currentPage={normalizedMasterPage}
        itemsPerPage={filteredMasterRows.length}
        totalItems={totalMasterPages}
        setPageNumber={setMasterPage}
        previouPageClick={() => setMasterPage((page) => Math.max((parseInt(page, 10) || 1) - 1, 1))}
        nextPageClick={() => setMasterPage((page) => Math.min((parseInt(page, 10) || 1) + 1, totalMasterPages))}
        disablePrevious={normalizedMasterPage <= 1}
        disableNext={normalizedMasterPage >= totalMasterPages}
      />
        </>
      )}
    </div>
  );

  return (
    <Box className={styles.page}>
      <Typography className={styles.heading}>Unified Onboarding Platform</Typography>
      <Tabs
        value={tabValue}
        onChange={(event, nextValue) => setTabValue(nextValue)}
        className={styles.tabs}
        TabIndicatorProps={{ style: { height: "3px", backgroundColor: "#3b86c7", borderRadius: "2px" } }}
      >
        <Tab label="Initiate Onboarding" />
        <Tab label="Unified Onboarding Master" />
      </Tabs>
      <TabPanel value={tabValue} index={0}>
        {renderInitiateOnboarding()}
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        {renderUnifiedMaster()}
      </TabPanel>
      <Modal open={Boolean(selectedDocumentViewer)} onClose={() => setSelectedDocumentViewer(null)}>
        <Box className={styles.documentModalBox}>
          <div className={styles.documentModalHeader}>
            <div>
              <h3>{displayValue(selectedDocumentViewer?.label)}</h3>
              <p>{displayValue(selectedDocumentViewer?.fileName || selectedDocumentViewer?.source)}</p>
            </div>
            <div className={styles.documentModalActions}>
              {selectedDocumentViewer?.source ? (
                <Tooltip title="Open in new tab" arrow>
                  <IconButton size="small" onClick={() => window.open(selectedDocumentViewer.source, "_blank", "noreferrer")}>
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              <IconButton size="small" onClick={() => setSelectedDocumentViewer(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
          <div className={styles.documentModalBody}>
            {selectedDocumentViewer?.source && getDocumentType(selectedDocumentViewer) === "image" ? (
              <img src={selectedDocumentViewer.source} alt={selectedDocumentViewer.label} />
            ) : selectedDocumentViewer?.source ? (
              <iframe src={selectedDocumentViewer.source} title={selectedDocumentViewer.label || "Document preview"} />
            ) : (
              <p className={styles.emptyText}>Document preview is not available.</p>
            )}
          </div>
        </Box>
      </Modal>
      <Modal open={invitePopup.open} onClose={closeInvitePopup}>
        <Box>
          <SuccessPopupDashboard
            popupImage={invitePopup.success ? successPopupImg : failurePopupImg}
            popupHeroText={invitePopup.success ? <>Invite Sent Successfully!</> : <>Invite Failed</>}
            popupDescription={invitePopup.message}
            popBtn={true}
            btnText={"Okay"}
            btnClick={closeInvitePopup}
            buyerBtn={true}
            buyerBtnOutline={true}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default UnifiedOnboarding;
